/**
 * Help Center Content
 * 
 * Structured knowledge base content for the in-app Help Center.
 * Each section and article is tagged with roles to enable role-based filtering.
 * 
 * Role hierarchy:
 * - 'basic' (Agent): Sees only articles tagged with 'basic'
 * - 'team_lead': Sees 'basic' + 'team_lead' articles
 * - 'admin': Sees everything
 */

const ALL_ROLES = ['basic', 'team_lead', 'admin'];
const LEAD_AND_ADMIN = ['team_lead', 'admin'];
const ADMIN_ONLY = ['admin'];

const helpSections = [
  // ─────────────────────────────────────────────
  // 1. GETTING STARTED
  // ─────────────────────────────────────────────
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: '🚀',
    roles: ALL_ROLES,
    articles: [
      {
        id: 'welcome',
        title: 'Welcome to TreeGenie',
        roles: ALL_ROLES,
        keywords: ['overview', 'introduction', 'what is', 'about'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-script-overview.png',
            alt: 'TreeGenie call script page showing the sidebar navigation, conversation script, and response buttons',
            caption: 'TreeGenie — your all-in-one call script and performance platform'
          },
          {
            type: 'paragraph',
            text: 'TreeGenie is a call script management platform designed for sales teams and call centers. It helps you follow structured conversation scripts, capture leads, and improve performance through AI-powered call evaluation.'
          },
          {
            type: 'heading',
            text: 'What You Can Do'
          },
          {
            type: 'list',
            items: [
              'Follow interactive call scripts with guided conversation flows and objection handling',
              'Capture lead information at the end of each call',
              'Upload recorded calls for AI-powered transcription and scoring',
              'Track your personal performance and see how you rank on the leaderboard'
            ]
          },
          {
            type: 'note',
            text: 'Team Leads have additional tools: Script Builder for creating scripts, Leads page for managing all team leads, Marketplace for buying and selling scripts, and Team Manager for inviting and managing agents.'
          },
          {
            type: 'heading',
            text: 'Quick Navigation'
          },
          {
            type: 'list',
            items: [
              '📞 Call Script — Follow your script and save leads',
              '🎯 AI Call Evaluator — Upload and score recorded calls',
              '📊 Dashboard / Command Center — View your performance scorecard and team stats',
              '⚙️ Settings — Manage your account'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'getting-started', articleId: 'understanding-roles', title: 'Your Role as an Agent' },
              { sectionId: 'getting-started', articleId: 'navigating-the-app', title: 'Your Sidebar & Navigation' },
              { sectionId: 'call-script', articleId: 'call-script-overview', title: 'Call Script Overview' }
            ]
          }
        ]
      },
      {
        id: 'understanding-roles',
        title: 'Your Role as an Agent',
        roles: ['basic'],
        keywords: ['role', 'permissions', 'access', 'agent', 'team lead', 'admin'],
        content: [
          {
            type: 'image',
            src: '/help-images/sidebar-navigation-agent.png',
            alt: 'Agent sidebar navigation highlighted in a red box — shows sections: Call Script, AI Call Evaluator, Dashboard, My Leads, Settings, and Help Center',
            caption: 'Your sidebar (highlighted in red) — Agents see Call Script, AI Call Evaluator, Dashboard, My Leads, Settings, and Help Center',
          },
          {
            type: 'paragraph',
            text: 'Your role determines what features you can access in TreeGenie.'
          },
          {
            type: 'heading',
            text: 'As an Agent, you can:'
          },
          {
            type: 'list',
            items: [
              'Use the Call Script page to follow scripts and make calls',
              'Save leads with prospect information after each call',
              'Upload call recordings for AI evaluation',
              'View your own evaluation scores and personal performance analytics',
              'See your position on the team leaderboard on your Dashboard',
              'Manage your account settings'
            ]
          },
          {
            type: 'note',
            text: 'Your role badge (Agent) is displayed in the sidebar beneath your name. If you think you have the wrong role, contact your Team Lead.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'getting-started', articleId: 'navigating-the-app', title: 'Navigating the App' },
              { sectionId: 'call-script', articleId: 'call-script-overview', title: 'Call Script Overview' }
            ]
          }
        ]
      },
      {
        id: 'understanding-roles-lead',
        title: 'Understanding Roles & Permissions',
        roles: LEAD_AND_ADMIN,
        keywords: ['role', 'permissions', 'access', 'agent', 'team lead'],
        content: [
          {
            type: 'image',
            src: '/help-images/sidebar-navigation.png',
            alt: 'Team Lead sidebar highlighted in a red box — shows full navigation including Script Builder, Command Center, Leads, Marketplace, and Team Manager',
            caption: 'Team Lead sidebar (highlighted in red) — includes Script Builder, Command Center, Leads, Marketplace, and Team Manager',
          },
          {
            type: 'paragraph',
            text: 'TreeGenie has two roles, each with different levels of access. Your role is shown as a badge in the sidebar beneath your name.'
          },
          {
            type: 'heading',
            text: 'Agent'
          },
          {
            type: 'list',
            items: [
              'Call Script — follow scripts and make calls',
              'Save leads after each call',
              'Upload call recordings for AI evaluation',
              'View personal evaluation scores and performance analytics',
              'See personal stats and team leaderboard on their Dashboard'
            ]
          },
          {
            type: 'heading',
            text: 'Team Lead'
          },
          {
            type: 'paragraph',
            text: 'In addition to Agent features, Team Leads can:'
          },
          {
            type: 'list',
            items: [
              'Create and edit call scripts using the Script Builder or Flowchart Editor',
              'Generate and improve scripts using AI from evaluation data',
              'View and manage all team leads on the Leads page',
              'Browse, purchase, and sell scripts on the Marketplace',
              'Invite and manage team members, assign scripts and roles',
              'Customize team branding (logo, colors, fonts, custom login URL)',
              'Manage subscription and billing, purchase evaluation credit packs',
              'Monitor team performance via the Command Center'
            ]
          },
          {
            type: 'note',
            text: 'Role badges are displayed in the sidebar beneath each user\'s name. Change a user\'s role at any time from Team Manager.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'getting-started', articleId: 'navigating-the-app-lead', title: 'Navigating the App' },
              { sectionId: 'team-manager', articleId: 'managing-team-members', title: 'Managing Team Members' }
            ]
          }
        ]
      },
      {
        id: 'navigating-the-app',
        title: 'Your Sidebar & Navigation',
        roles: ['basic'],
        keywords: ['navigation', 'sidebar', 'menu', 'pages', 'breadcrumbs'],
        content: [
          {
            type: 'image',
            src: '/help-images/sidebar-navigation-agent.png',
            alt: 'Agent sidebar highlighted in a red box — shows TreeGenie logo, your name and Agent badge, and navigation items for Call Script, AI Call Evaluator, Dashboard, My Leads, Settings, and Help Center',
            caption: 'Agent sidebar (highlighted) — your navigation includes Call Script, AI Call Evaluator, Dashboard, My Leads, Settings, and Help Center',
          },
          {
            type: 'paragraph',
            text: 'The app uses a left sidebar for navigation. Your sidebar shows the pages and tools available to you as an Agent.'
          },
          {
            type: 'heading',
            text: 'Your Sidebar'
          },
          {
            type: 'list',
            items: [
              'Calling — Call Script (your main calling workspace)',
              'Tools — AI Call Evaluator',
              'Management — Dashboard (your personal performance scorecard)',
              'System — Settings, Help Center'
            ]
          },
          {
            type: 'heading',
            text: 'User Profile'
          },
          {
            type: 'paragraph',
            text: 'Your name, role badge (Agent), and team name are displayed at the top of the sidebar below the company logo. For example, at City Realtors you\'d see your name and "Agent" below the team logo.'
          },
          {
            type: 'heading',
            text: 'Send Feedback'
          },
          {
            type: 'paragraph',
            text: 'At the bottom of the sidebar, there\'s a "Send Feedback" button. Use it to report bugs, request features, or share general feedback. It automatically includes a screenshot and helpful context so the team can follow up.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'troubleshooting', articleId: 'reporting-problems', title: 'Reporting a Problem' },
              { sectionId: 'settings', articleId: 'account-settings', title: 'Account Settings' }
            ]
          }
        ]
      },
      {
        id: 'navigating-the-app-lead',
        title: 'Navigating the App (Team Lead)',
        roles: LEAD_AND_ADMIN,
        keywords: ['navigation', 'sidebar', 'menu', 'pages', 'breadcrumbs', 'command center'],
        content: [
          {
            type: 'image',
            src: '/help-images/sidebar-navigation.png',
            alt: 'Team Lead sidebar with Calling, Tools (Call Evaluator, Script Builder), Management (Command Center, Leads, Marketplace, Team Manager), and System sections',
            caption: 'Team Lead sidebar (highlighted in red) — includes all sections: Script Builder, Command Center, Leads, Marketplace, and Team Manager',
          },
          {
            type: 'paragraph',
            text: 'The app uses a left sidebar for navigation. The sidebar is organized into sections, and which items you see depends on your role.'
          },
          {
            type: 'heading',
            text: 'Sidebar Sections'
          },
          {
            type: 'list',
            items: [
              'Calling — Call Script',
              'Tools — AI Call Evaluator, Script Builder',
              'Management — Command Center (team KPIs, Agent Pulse, Peak Hours), Leads, Marketplace, Team Manager',
              'System — Settings, Help Center'
            ]
          },
          {
            type: 'heading',
            text: 'User Profile'
          },
          {
            type: 'paragraph',
            text: 'Your name, role badge (Team Lead), and team name are displayed at the top of the sidebar below the company logo. For example, Dave Smith at City Realtors would see "Dave Smith" with a "Team Lead" badge.'
          },
          {
            type: 'heading',
            text: 'Breadcrumbs'
          },
          {
            type: 'paragraph',
            text: 'Breadcrumbs appear at the top of each page showing your current location (e.g., Home > Script Builder > Edit Script). Click any breadcrumb to navigate back.'
          },
          {
            type: 'heading',
            text: 'Send Feedback'
          },
          {
            type: 'paragraph',
            text: 'At the bottom of the sidebar, there\'s a "Send Feedback" button. Use it to report bugs, request features, or share general feedback. It automatically includes a screenshot and helpful context so the team can follow up.'
          },
          {
            type: 'tip',
            text: 'Use breadcrumbs to quickly jump back to a parent page instead of using the sidebar. This is especially handy when editing scripts or viewing evaluation reports.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'troubleshooting', articleId: 'reporting-problems', title: 'Reporting a Problem' },
              { sectionId: 'settings', articleId: 'account-settings', title: 'Account Settings' }
            ]
          }
        ]
      },
      {
        id: 'logging-in',
        title: 'Logging In / First Login',
        roles: ['basic'],
        keywords: ['login', 'sign in', 'first time', 'password', 'access', 'invite', 'account'],
        content: [
          {
            type: 'heading',
            text: 'Signing In'
          },
          {
            type: 'paragraph',
            text: 'To access TreeGenie, visit the login page (your Team Lead will give you the URL, for example: test.treegenie.ai). You\'ll see a "Sign In" button on the landing page.'
          },
          {
            type: 'steps',
            items: [
              'Go to the TreeGenie URL provided by your Team Lead',
              'Click the "Sign In" button on the welcome screen',
              'Enter the email address your Team Lead used to invite you — it must match exactly',
              'Enter your password, then click "Sign In"',
              'If it\'s your first time, use the password you set when you accepted your invitation'
            ]
          },
          {
            type: 'heading',
            text: 'First Login (New Accounts)'
          },
          {
            type: 'paragraph',
            text: 'When your Team Lead invites you, you\'ll receive an email with a link to set up your account. Click the link, create a password, and you\'ll be signed in automatically.'
          },
          {
            type: 'note',
            text: 'Your login email must be the same email address your Team Lead entered when inviting you. If you get an error saying "Invitation not found," check with your Team Lead that they used the correct email address.'
          },
          {
            type: 'heading',
            text: 'Forgot Your Password?'
          },
          {
            type: 'steps',
            items: [
              'On the Sign In screen, click "Forgot your password?" (appears below the password field)',
              'Enter your email address and click "Send Reset Email"',
              'Check your inbox for an email from TreeGenie (also check your spam/junk folder)',
              'Click the link in the email — it takes you to a page where you can set a new password',
              'Set your new password and click Save, then sign in as usual'
            ]
          },
          {
            type: 'tip',
            text: 'Password reset links expire after 1 hour. If your link has expired, go back to the login page and request a new one.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'getting-started', articleId: 'understanding-roles', title: 'Your Role as an Agent' },
              { sectionId: 'troubleshooting', articleId: 'permission-errors', title: 'Permission Errors' },
              { sectionId: 'troubleshooting', articleId: 'forgot-password', title: 'Forgot Password' },
              { sectionId: 'troubleshooting', articleId: 'signed-out-unexpectedly', title: 'Getting Signed Out Unexpectedly' }
            ]
          }
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────
  // 2. CALL SCRIPT
  // ─────────────────────────────────────────────
  {
    id: 'call-script',
    title: 'Call Script',
    icon: '📞',
    roles: ALL_ROLES,
    articles: [
      {
        id: 'call-script-overview',
        title: 'Call Script Overview',
        roles: ALL_ROLES,
        keywords: ['call', 'script', 'overview', 'layout', 'interface'],
        content: [
          {
            type: 'paragraph',
            text: 'The Call Script page is where you make your calls. It shows you exactly what to say, how the prospect might respond, and guides you through the full conversation step by step.'
          },
          {
            type: 'image',
            src: '/help-images/call-script-overview.png',
            alt: 'Call Script page showing the agent script text in a blue box, prospect response buttons, step progress indicator, conversation history panel, and coaching notes at the bottom',
            caption: 'The Call Script page — your guide for every call'
          },
          {
            type: 'heading',
            text: 'What You See on Screen'
          },
          {
            type: 'list',
            items: [
              '🔵 Blue script box — The exact text to read to your prospect. It changes with every step.',
              '🔘 Response buttons — Click the button that matches what your prospect says to move to the next step',
              '📊 Step progress bar — Shows where you are in the conversation (e.g., "Step 3 of 8")',
              '📜 Conversation history — A running log of everything said during the call',
              '🟡 Coaching notes — Yellow box at the bottom with tips and reminders for that specific step (see below)',
              '📋 Lead form — Appears at the end when it\'s time to save the prospect\'s information'
            ]
          },
          {
            type: 'image',
            src: '/help-images/call-script-coaching.png',
            alt: 'Call Script page scrolled down to show the yellow coaching notes box with tips and guidance for the current step',
            caption: 'Coaching notes appear in the yellow box — helpful tips written by your Team Lead for each part of the call'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'saving-leads', title: 'Saving a Lead' },
              { sectionId: 'troubleshooting', articleId: 'wrong-or-missing-script', title: 'Call Script Shows Wrong or No Script' },
              { sectionId: 'troubleshooting', articleId: 'cant-save-lead', title: 'Can\'t Save a Lead' }
            ]
          }
        ]
      },
      {
        id: 'following-the-script',
        title: 'Following the Script',
        roles: ALL_ROLES,
        keywords: ['nodes', 'responses', 'flow', 'conversation', 'navigate', 'buttons'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-script-following.png',
            alt: 'Call Script page mid-conversation showing blue agent script text, response buttons below, and step progress indicator',
            caption: 'Mid-call view — the blue box shows what to say, buttons below show what the prospect might answer'
          },
          {
            type: 'paragraph',
            text: 'The script is built from nodes (what you say) and responses (how the prospect might answer). Follow the script step by step:'
          },
          {
            type: 'heading',
            text: 'How It Works'
          },
          {
            type: 'steps',
            items: [
              'The script starts at the opening node with a greeting or introduction',
              'Read the blue "Agent Script" box text to the prospect',
              'Below the script, you\'ll see response buttons representing common prospect answers',
              'Click the button that best matches what the prospect said — the script advances to the next node',
              'Some responses require text input — type the prospect\'s answer and click Submit',
              'Continue until you reach the end of the call'
            ]
          },
          {
            type: 'heading',
            text: 'Text Input Nodes'
          },
          {
            type: 'paragraph',
            text: 'Some nodes ask you to type a free-form answer instead of clicking a button. For example, a node might say "Enter the prospect\'s preferred callback time." These nodes show a text box and a Submit button instead of response buttons.'
          },
          {
            type: 'steps',
            items: [
              'A text box appears under the script — type the prospect\'s response exactly as they said it',
              'Click the Submit button (or press Enter) to save the answer and move to the next node',
              'The typed text is saved to the lead record and conversation history automatically'
            ]
          },
          {
            type: 'heading',
            text: 'Clicked the Wrong Button?'
          },
          {
            type: 'paragraph',
            text: 'Don\'t worry — it happens. Use the Back button to go back one step and click the correct response.'
          },
          {
            type: 'list',
            items: [
              'Back button — Appears at the top-right after the first step; takes you back one step',
              'Reset button — Starts the entire call from the beginning; shows a confirmation message first so you won\'t lose your place by accident'
            ]
          },
          {
            type: 'tip',
            text: 'If you click Back and go too far, just click the correct response button again to move forward. The conversation history is updated to reflect your actual path.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'coaching-notes', title: 'Coaching Notes' },
              { sectionId: 'call-script', articleId: 'objection-handling', title: 'Handling Objections' }
            ]
          }
        ]
      },
      {
        id: 'objection-handling',
        title: 'Handling Objections',
        roles: ALL_ROLES,
        keywords: ['objection', 'objections', 'handle', 'overcome', 'red buttons'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-script-objections.png',
            alt: 'Call Script page with the Common Objections section highlighted in a red box — red objection buttons appear below the blue response buttons',
            caption: 'The objection section (highlighted) — red "Common Objections" buttons appear below the regular blue response buttons'
          },
          {
            type: 'paragraph',
            text: 'When a prospect raises an objection, the script provides pre-built responses to help you handle it effectively.'
          },
          {
            type: 'heading',
            text: 'How Objections Work'
          },
          {
            type: 'list',
            items: [
              'Objection buttons appear in red below the regular response buttons, labeled "Common Objections"',
              'Click the objection button that matches what the prospect said',
              'The script jumps to a specialized objection-handling node with guidance on how to respond',
              'After handling the objection, you\'ll see options to continue with the current question or end the call',
              'The objection and your response are recorded in the conversation history'
            ]
          },
          {
            type: 'heading',
            text: 'Coaching Notes for Objections'
          },
          {
            type: 'paragraph',
            text: 'Coaching notes (yellow box at the bottom of the screen) often include tips specifically for handling the current objection, such as tone suggestions and rebuttals.'
          },
          {
            type: 'tip',
            text: 'Objection buttons only appear for the current script phase, so you\'ll see the most relevant objections at each stage of the call.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'coaching-notes', title: 'Coaching Notes' },
              { sectionId: 'script-builder', articleId: 'phases-and-objections', title: 'Phases & Objections (Script Builder)' }
            ]
          }
        ]
      },
      {
        id: 'coaching-notes',
        title: 'Coaching Notes',
        roles: ALL_ROLES,
        keywords: ['coaching', 'notes', 'tips', 'guidance', 'yellow', 'help'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-script-coaching.png',
            alt: 'Bottom of the Call Script page showing a yellow coaching notes box with guidance text visible for the current script node',
            caption: 'Coaching notes appear as a yellow bar fixed at the bottom of the screen — your private in-call guidance'
          },
          {
            type: 'paragraph',
            text: 'Coaching notes are private tips that appear at the bottom of the screen while you\'re on a call. Prospects cannot see or hear them — they\'re for your eyes only.'
          },
          {
            type: 'heading',
            text: 'What They Look Like'
          },
          {
            type: 'paragraph',
            text: 'Coaching notes appear as a yellow or amber box at the very bottom of the screen. The box shows a small lightbulb icon (💡) to make it easy to spot.'
          },
          {
            type: 'heading',
            text: 'What They Include'
          },
          {
            type: 'list',
            items: [
              'Tips on tone and delivery — e.g., "Speak confidently and pause after asking the question"',
              'Suggested rebuttals for common objections at this stage',
              'Reminders about key points to emphasize',
              'Guidance on what to listen for in the prospect\'s answer',
              'Notes on why a particular question matters in the conversation flow'
            ]
          },
          {
            type: 'heading',
            text: 'Expanding & Collapsing'
          },
          {
            type: 'paragraph',
            text: 'If the coaching note is long, it may be collapsed by default. Click the yellow box or the arrow icon to expand it and read the full note. Click again to collapse it back to save screen space.'
          },
          {
            type: 'note',
            text: 'Not every step will have a coaching note — they only appear when your Team Lead has added guidance for that specific part of the script. If you don\'t see a yellow box, that node has no coaching note.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'objection-handling', title: 'Handling Objections' },
              { sectionId: 'script-builder', articleId: 'editing-scripts', title: 'Editing Scripts (Node Editor)' }
            ]
          }
        ]
      },
      {
        id: 'conversation-history',
        title: 'Conversation History',
        roles: ALL_ROLES,
        keywords: ['conversation', 'history', 'panel', 'tracking', 'messages'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-script-history.png',
            alt: 'Call Script page showing the conversation history panel with agent messages in blue on the right and prospect messages on the left',
            caption: 'The conversation history panel — everything said during the call is recorded here in real time'
          },
          {
            type: 'paragraph',
            text: 'The conversation history tracks every step of the call as you go, recording both what you said and the prospect\'s responses.'
          },
          {
            type: 'heading',
            text: 'Two Layout Options'
          },
          {
            type: 'list',
            items: [
              'Top bar layout (default) — A compact horizontal bar at the top of the script area with a scrollable history box',
              'Side panel layout — A full-height panel on the right side of the screen with more room for longer conversations',
              'Toggle between layouts using the layout switch button'
            ]
          },
          {
            type: 'heading',
            text: 'Message Display'
          },
          {
            type: 'list',
            items: [
              'Agent messages appear in blue on the right side',
              'Prospect messages appear in green on the left side',
              'The message count is shown in the header (e.g., "5 messages")',
              'The panel auto-scrolls to show the latest message'
            ]
          },
          {
            type: 'note',
            text: 'The full conversation history is saved with the lead when you complete the call, so Team Leads can review what was discussed.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'saving-leads', title: 'Saving a Lead' },
              { sectionId: 'leads', articleId: 'lead-detail-panel', title: 'Lead Detail Panel' }
            ]
          }
        ]
      },
      {
        id: 'saving-leads',
        title: 'Saving a Lead',
        roles: ALL_ROLES,
        keywords: ['lead', 'save', 'prospect', 'form', 'data', 'capture'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-script-save-lead.png',
            alt: 'Call Script page at end of call with the Lead Data capture form highlighted in a red box — shows fields for name, phone, email, and a Save & Start New Call button',
            caption: 'The Lead Data form (highlighted) — appears after the call ends; fill in the prospect\'s details and click "Save & Start New Call"'
          },
          {
            type: 'paragraph',
            text: 'When you reach the end of the script, a "Call Complete" screen appears followed by the lead capture form.'
          },
          {
            type: 'heading',
            text: 'Lead Capture Form'
          },
          {
            type: 'list',
            items: [
              'The form title and fields are configured by your Team Lead',
              'Default fields include: Agent Name, Contact Name, Company, and Phone Number',
              'Required fields are marked with a red asterisk (*)',
              'Your team may have custom fields specific to your business',
              'Fill in the prospect\'s information as you gathered it during the conversation'
            ]
          },
          {
            type: 'heading',
            text: 'Save & Start New Call'
          },
          {
            type: 'steps',
            items: [
              'Fill in all required fields (marked with a red asterisk *)',
              'Click the "Save & Start New Call" button at the bottom of the form',
              'A green success notification appears confirming the lead was saved',
              'The script automatically resets so you\'re ready for your next call immediately'
            ]
          },
          {
            type: 'heading',
            text: 'If the Save Button is Greyed Out'
          },
          {
            type: 'paragraph',
            text: 'The "Save & Start New Call" button will appear faded (greyed out) until all required fields are filled in. This prevents accidentally saving a lead with missing information.'
          },
          {
            type: 'list',
            items: [
              'Check for any fields marked with a red asterisk (*) — these are required',
              'Make sure required fields are not empty and not just containing spaces',
              'Once all required fields have content, the Save button will become active (turns blue)'
            ]
          },
          {
            type: 'heading',
            text: 'What Happens If You Navigate Away Before Saving?'
          },
          {
            type: 'paragraph',
            text: 'If you leave the Call Script page before clicking Save, the lead data will not be saved. Always complete the Save step before switching pages or closing the browser.'
          },
          {
            type: 'warning',
            text: 'Do not close the browser tab or navigate to another page while the lead form is showing. Your call progress and the form data will be lost and cannot be recovered.'
          },
          {
            type: 'note',
            text: 'The lead data, full conversation history, and session details are all saved together in one step. Team Leads can view the complete record on the Leads page, including every response the prospect gave.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'conversation-history', title: 'Conversation History' },
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call for Evaluation' },
              { sectionId: 'troubleshooting', articleId: 'cant-save-lead', title: 'Can\'t Save a Lead' }
            ]
          }
        ]
      },
      {
        id: 'switching-scripts',
        title: 'Switching Scripts',
        roles: LEAD_AND_ADMIN,
        keywords: ['template', 'switch', 'change', 'select', 'script', 'default'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-script-switch.png',
            alt: 'Call Script header showing the template dropdown with a list of available scripts to switch between',
            caption: 'The template dropdown in the Call Script header — switch scripts at any time'
          },
          {
            type: 'paragraph',
            text: 'Team Leads can switch between available scripts using the template dropdown in the Call Script header.'
          },
          {
            type: 'heading',
            text: 'How to Switch'
          },
          {
            type: 'list',
            items: [
              'Click the template dropdown in the top-right of the Call Script page header',
              'Select the script you want to use from the list',
              'The script reloads immediately with the selected template'
            ]
          },
          {
            type: 'heading',
            text: 'Setting a Default Script'
          },
          {
            type: 'list',
            items: [
              'After selecting a script, a "Set Default" button appears next to the dropdown',
              'Click "Set Default" to make this the default script for agents on your team',
              'Agents see only their assigned script and cannot switch templates'
            ]
          },
          {
            type: 'heading',
            text: 'Agent Template Assignments'
          },
          {
            type: 'paragraph',
            text: 'When you assign a new template to an agent in Team Manager, they\'ll see a "Next call" indicator showing which script will load on their next call.'
          },
          {
            type: 'note',
            text: 'Agents see only their assigned script and cannot switch templates. To change an agent\'s script, update their template assignment in Team Manager.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'my-scripts-list', title: 'My Scripts Dashboard' },
              { sectionId: 'team-manager', articleId: 'managing-team-members', title: 'Managing Team Members' }
            ]
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 3. AI CALL EVALUATOR
  // ─────────────────────────────────────────────
  {
    id: 'call-evaluator',
    title: 'AI Call Evaluator',
    icon: '🎙️',
    roles: ALL_ROLES,
    articles: [
      {
        id: 'evaluator-overview',
        title: 'Call Evaluator Overview',
        roles: ['basic'],
        keywords: ['evaluator', 'overview', 'tabs', 'ai'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-evaluator-overview.png',
            alt: 'Call Evaluator page showing the Upload Call tab with form fields for salesperson name, prospect name, call type, and audio file upload area',
            caption: 'The Call Evaluator — upload a recorded call to get an instant AI-powered score and feedback'
          },
          {
            type: 'paragraph',
            text: 'The AI Call Evaluator lets you upload recorded calls for AI-powered analysis. It transcribes the call, scores your performance across multiple categories, and provides actionable feedback.'
          },
          {
            type: 'heading',
            text: 'Tabs'
          },
          {
            type: 'list',
            items: [
              'Upload Call — Upload and submit audio files for evaluation',
              'My Evaluations — View all your evaluated calls with filters and details',
              'My Performance — Your personal analytics dashboard with scores, trends, and category breakdowns'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call' },
              { sectionId: 'troubleshooting', articleId: 'audio-upload-problems', title: 'Audio Upload Problems' }
            ]
          }
        ]
      },
      {
        id: 'evaluator-overview-lead',
        title: 'Call Evaluator — Team Features',
        roles: LEAD_AND_ADMIN,
        keywords: ['evaluator', 'overview', 'tabs', 'ai', 'team performance'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-evaluator-overview.png',
            alt: 'Call Evaluator page showing the Upload Call, Evaluations, and Team Performance tabs',
            caption: 'The Call Evaluator — three tabs for uploading calls, reviewing evaluations, and monitoring team performance'
          },
          {
            type: 'paragraph',
            text: 'The AI Call Evaluator lets you and your agents upload recorded calls for AI-powered analysis. It transcribes calls, scores performance across multiple categories, and provides actionable feedback.'
          },
          {
            type: 'heading',
            text: 'Tabs'
          },
          {
            type: 'list',
            items: [
              'Upload Call — Upload and submit audio files for evaluation',
              'Evaluations — View all team evaluations with filters, linking, export, and PDF download',
              'Team Performance — Team-wide analytics dashboard with leaderboard, category scores, and per-agent drill-down'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call' },
              { sectionId: 'call-evaluator', articleId: 'team-performance-analytics', title: 'Team Performance Analytics' },
              { sectionId: 'troubleshooting', articleId: 'audio-upload-problems', title: 'Audio Upload Problems' }
            ]
          }
        ]
      },
      {
        id: 'uploading-calls',
        title: 'Uploading a Call',
        roles: ALL_ROLES,
        keywords: ['upload', 'audio', 'recording', 'evaluate', 'file', 'mp3', 'wav'],
        content: [
          {
            type: 'paragraph',
            text: 'Upload recorded calls to get AI-powered transcription, scoring, and feedback.'
          },
          {
            type: 'image',
            src: '/help-images/call-evaluator-upload.png',
            alt: 'Call Evaluator Upload Call tab showing salesperson name field (pre-filled for agents), prospect name field, call type dropdown, link to lead selector, audio file drop zone, and Upload and Analyze Call button',
            caption: 'The Upload Call form — your name is pre-filled, enter the prospect\'s name, select call type, and upload your recording'
          },
          {
            type: 'heading',
            text: 'Upload Form Fields'
          },
          {
            type: 'list',
            items: [
              'Salesperson — Your name is pre-filled automatically (Team Leads can select any team member)',
              'Prospect Name — Enter the prospect\'s name (required)',
              'Call Type — Select the type of call from the dropdown (e.g., Cold Call, Follow-up)',
              'Link to Lead — Optionally link this evaluation to an existing lead',
              'Audio File — Drag and drop or click to browse. Accepted formats: MP3, WAV, M4A (max 100MB)'
            ]
          },
          {
            type: 'heading',
            text: 'Processing'
          },
          {
            type: 'list',
            items: [
              'After clicking "Upload and Analyze Call," you\'ll see a progress bar',
              'Processing goes through 4 stages: Transcribing audio, Analyzing conversation, Generating evaluation, Complete',
              'This typically takes 2-5 minutes depending on call length',
              'You can navigate away and come back — results appear in the Evaluations tab when ready'
            ]
          },
          {
            type: 'heading',
            text: 'Evaluation Credits'
          },
          {
            type: 'paragraph',
            text: 'Each evaluation uses one credit from your monthly allowance. A usage indicator at the top of the upload form shows how many credits remain.'
          },
          {
            type: 'warning',
            text: 'When credits run low, you\'ll see a warning on the upload form. Team Leads can purchase additional credit packs from the Subscription settings without needing to upgrade plans.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'settings', articleId: 'subscription-billing', title: 'Subscription & Billing' },
              { sectionId: 'call-evaluator', articleId: 'linking-evaluations-to-leads', title: 'Linking Evaluations to Leads' },
              { sectionId: 'troubleshooting', articleId: 'audio-upload-problems', title: 'Audio Upload Problems' }
            ]
          }
        ]
      },
      {
        id: 'viewing-evaluations',
        title: 'Viewing My Evaluations',
        roles: ALL_ROLES,
        keywords: ['evaluations', 'list', 'filter', 'view', 'results', 'history', 'csv', 'export', 'my evaluations'],
        content: [
          {
            type: 'paragraph',
            text: 'The Evaluations tab shows your evaluated calls. As an Agent, you see only your own evaluations. Team Leads see evaluations for the entire team.'
          },
          {
            type: 'image',
            src: '/help-images/call-evaluator-evaluations.png',
            alt: 'Evaluations tab showing a list of evaluated calls with columns for date, prospect name, call type, score, status, linked lead, and action buttons to view report, download PDF, or delete',
            caption: 'The Evaluations tab — view and manage all your evaluated calls'
          },
          {
            type: 'heading',
            text: 'Filters'
          },
          {
            type: 'list',
            items: [
              'Click "Show Filters" to expand the filter panel',
              'Call Type — Filter by call type (Cold Call, Follow-up, etc.)',
              'Score Range — Filter by grade (Excellent 90-100, Good 80-89, Fair 70-79, Needs Work 60-69, Poor 0-59)',
              'Date Range — Set "From" and "To" dates',
              'Use "Clear All Filters" to reset'
            ]
          },
          {
            type: 'heading',
            text: 'Table Columns'
          },
          {
            type: 'list',
            items: [
              'Date — Relative time (e.g., "2h ago") or full date',
              'Prospect — Prospect name',
              'Type — Call type badge',
              'Score — Overall score out of 100',
              'Status — Processing status (Uploading, Transcribing, Analyzing, Completed, Error)',
              'Actions — View report, Download PDF, Delete',
              'Expires — Days remaining before auto-deletion (evaluations expire after 90 days)'
            ]
          },
          {
            type: 'heading',
            text: 'Actions'
          },
          {
            type: 'list',
            items: [
              'Click any row to view the full evaluation report',
              'Click the PDF button to download the evaluation as a PDF',
              'Click the trash icon to delete your own evaluation (with confirmation)'
            ]
          },
          {
            type: 'warning',
            text: 'Evaluations expire after 90 days and are automatically deleted. Download the PDF to keep a permanent copy.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'evaluation-report', title: 'Understanding the Evaluation Report' },
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call' }
            ]
          }
        ]
      },
      {
        id: 'viewing-evaluations-team',
        title: 'Managing Team Evaluations',
        roles: LEAD_AND_ADMIN,
        keywords: ['evaluations', 'team', 'filter', 'salesperson', 'export', 'csv', 'link', 'lead'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-evaluator-team.png',
            alt: 'Evaluations tab showing the full team list with columns for Date, Salesperson, Prospect, Type, Score, Status, Lead link, and action buttons',
            caption: 'Team Evaluations — see all your team\'s evaluated calls in one place, filter by agent or date, and export to CSV'
          },
          {
            type: 'paragraph',
            text: 'The Evaluations tab gives Team Leads a full view of all call evaluations across the team. Use filters to drill into specific agents, date ranges, call types, or score ranges.'
          },
          {
            type: 'heading',
            text: 'Additional Filters (Team Leads)'
          },
          {
            type: 'list',
            items: [
              'Salesperson — Filter by a specific team member\'s evaluations',
              'Call Type — Filter by call type (Cold Call, Follow-up, etc.)',
              'Score Range — Filter by grade (Excellent 90-100, Good 80-89, Fair 70-79, Needs Work 60-69, Poor 0-59)',
              'Date Range — Set "From" and "To" dates',
              'Use "Clear All Filters" to reset'
            ]
          },
          {
            type: 'heading',
            text: 'Team-Specific Table Columns'
          },
          {
            type: 'list',
            items: [
              'Salesperson — Which agent made the call',
              'Lead — Link or unlink the evaluation to a lead record',
              'All standard columns: Date, Prospect, Type, Score, Status, Expires'
            ]
          },
          {
            type: 'heading',
            text: 'Team Actions'
          },
          {
            type: 'list',
            items: [
              'Link evaluations to leads using the "Link" button in the Lead column',
              'Use "Export to CSV" to download the filtered list as a spreadsheet'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'linking-evaluations-to-leads', title: 'Linking Evaluations to Leads' },
              { sectionId: 'call-evaluator', articleId: 'team-performance-analytics', title: 'Team Performance Analytics' }
            ]
          }
        ]
      },
      {
        id: 'evaluation-report',
        title: 'Understanding the Evaluation Report',
        roles: ALL_ROLES,
        keywords: ['report', 'score', 'breakdown', 'categories', 'recommendations', 'transcript', 'feedback'],
        content: [
          {
            type: 'paragraph',
            text: 'Click any completed evaluation to open the full report. The report provides a comprehensive analysis of the call.'
          },
          {
            type: 'image',
            src: '/help-images/call-evaluator-report.png',
            alt: 'Evaluation report modal showing overall score with grade, salesperson name, call type, talking ratio bar, and final verdict from the AI',
            caption: 'Evaluation report — opens when you click "View" on any completed evaluation'
          },
          {
            type: 'image',
            src: '/help-images/call-evaluator-report-scores.png',
            alt: 'Score breakdown section of the evaluation report showing category scores out of 10 with color-coded bars and per-category feedback',
            caption: 'Score breakdown — see exactly how you performed on Opening, Rapport, Discovery, Objection Handling, Closing, and Script Adherence'
          },
          {
            type: 'heading',
            text: 'Report Sections'
          },
          {
            type: 'list',
            items: [
              'Overall Score — Your total score out of 100 with a grade',
              'Call Type — The type of call (e.g., Cold Call, Follow-up)',
              'Linked Lead — View the associated lead record',
              'Talking Ratio — Visual bar showing prospect vs. salesperson talk time',
              'Final Verdict — The AI\'s overall assessment of the call',
              'Score Breakdown — Scores for each category (e.g., Opening & Rapport, Needs Discovery, Presentation, Objection Handling, Closing) out of 10, with per-category feedback',
              'Summary — A narrative summary of the entire call',
              'Detailed Feedback — In-depth analysis by category',
              'Evaluation — Four columns: What They Did Well, Minor Improvements, Major Mistakes, Lost Connection Points',
              'Strategy Checklist — Pass/fail indicators for key sales techniques',
              'Key Tactics & Errors — Top 3 tactics observed and top 3 unforced errors',
              'Prospect Takeaways — Core problem, motivation, emotions, and timeline',
              'Missed Opportunities — 3 specific opportunities that were missed',
              'Tactical Next Steps — 3 actionable recommendations',
              'Final Learning — One key takeaway to focus on'
            ]
          },
          {
            type: 'heading',
            text: 'Download & Share'
          },
          {
            type: 'paragraph',
            text: 'Click "Download PDF" in the report header to save the full report as a PDF file for sharing or offline review.'
          },
          {
            type: 'tip',
            text: 'Focus on the "Final Learning" section at the bottom of each report — it highlights the single most impactful improvement to make for your next call.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'evaluator-analytics', title: 'My Performance Analytics' },
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call' }
            ]
          }
        ]
      },
      {
        id: 'evaluator-analytics',
        title: 'My Performance Analytics',
        roles: ['basic'],
        keywords: ['analytics', 'performance', 'charts', 'trends', 'metrics', 'my performance'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-evaluator-performance.png',
            alt: 'My Performance tab showing average score card, category performance grid with scores and progress bars, and recent calls list',
            caption: 'My Performance — your personal scorecard showing average scores by category and recent call history'
          },
          {
            type: 'paragraph',
            text: 'The My Performance tab shows your personal call evaluation metrics and trends over time.'
          },
          {
            type: 'heading',
            text: 'Date Range'
          },
          {
            type: 'paragraph',
            text: 'Select a time period: 7 Days, 30 Days (default), 90 Days, or All Time.'
          },
          {
            type: 'heading',
            text: 'What You\'ll See'
          },
          {
            type: 'list',
            items: [
              'Key Metrics — Your average score with a letter grade, total calls evaluated, score range, and trend arrow',
              'Category Performance — A grid of all scoring categories (e.g., Opening & Rapport, Objection Handling, Closing) with your average scores and progress bars',
              'Recent Calls — Your last 5 evaluated calls with scores and quick-access links to view the report or download the PDF'
            ]
          },
          {
            type: 'tip',
            text: 'Focus on the categories with the lowest scores — those are your biggest opportunities for improvement. Review the evaluation reports for specific coaching tips in those areas.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'evaluation-report', title: 'Understanding the Evaluation Report' },
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call' }
            ]
          }
        ]
      },
      {
        id: 'team-performance-analytics',
        title: 'Team Performance Analytics',
        roles: LEAD_AND_ADMIN,
        keywords: ['analytics', 'performance', 'charts', 'trends', 'metrics', 'dashboard', 'leaderboard', 'team performance'],
        content: [
          {
            type: 'paragraph',
            text: 'The Team Performance tab gives you a full overview of your team\'s evaluation scores and trends. Use it to identify top performers, coaching opportunities, and patterns across your agents.'
          },
          {
            type: 'image',
            src: '/help-images/call-evaluator-performance.png',
            alt: 'Team Performance tab showing time period filter, summary cards for Total Calls and Team Average Score, a ranked leaderboard of agents with scores and trends, and Quick Insights showing strongest and weakest categories',
            caption: 'The Team Performance tab — monitor team-wide scores, compare agents, and identify coaching opportunities'
          },
          {
            type: 'heading',
            text: 'Date Range'
          },
          {
            type: 'paragraph',
            text: 'Select a time period: 7 Days, 30 Days (default), 90 Days, or All Time.'
          },
          {
            type: 'heading',
            text: 'Team Dashboard Sections'
          },
          {
            type: 'list',
            items: [
              'Summary cards — Total Calls, Team Average Score, Top Performer, Score Trend',
              'Leaderboard — Ranked list of team members by average score, with rank badges, call counts, score ranges, and trend indicators',
              'Quick Insights — Strongest categories and categories needing improvement, shown as ranked lists with score bars'
            ]
          },
          {
            type: 'heading',
            text: 'Drilling into Individual Performance'
          },
          {
            type: 'paragraph',
            text: 'Click any agent in the leaderboard to view their individual performance breakdown — category scores, recent calls, and trends.'
          },
          {
            type: 'tip',
            text: 'Use the leaderboard regularly to identify agents who might benefit from one-on-one coaching. The Quick Insights section shows which categories need the most team-wide attention.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'evaluation-report', title: 'Understanding the Evaluation Report' },
              { sectionId: 'call-evaluator', articleId: 'linking-evaluations-to-leads', title: 'Linking Evaluations to Leads' }
            ]
          }
        ]
      },
      {
        id: 'linking-evaluations-to-leads',
        title: 'Linking Evaluations to Leads',
        roles: LEAD_AND_ADMIN,
        keywords: ['link', 'lead', 'evaluation', 'connect', 'associate'],
        content: [
          {
            type: 'image',
            src: '/help-images/call-evaluator-link-lead.png',
            alt: 'Evaluations list showing the Lead column with a Link button for unlinked evaluations and a green lead name for linked ones',
            caption: 'The Lead column in the Evaluations list — click Link to connect an evaluation to a lead record'
          },
          {
            type: 'paragraph',
            text: 'Link evaluations to leads to maintain a complete record of prospect interactions.'
          },
          {
            type: 'heading',
            text: 'How to Link'
          },
          {
            type: 'list',
            items: [
              'From the Evaluations list — Click the "Link" button in the Lead column, then select a lead from the dropdown',
              'From a report — Click "Link to a lead" in the Linked Lead section, then choose from recent leads',
              'During upload — Use the "Link to Lead" dropdown on the upload form before submitting',
              'The link is bidirectional — it appears on both the evaluation and the lead\'s detail panel',
              'To unlink, click the green "Unlink" button next to the linked lead name'
            ]
          },
          {
            type: 'note',
            text: 'The link is bidirectional — once linked, the evaluation score appears on the lead\'s detail panel, and the lead info appears in the evaluation report.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'leads', articleId: 'leads-overview', title: 'Leads Page Overview' },
              { sectionId: 'leads', articleId: 'lead-detail-panel', title: 'Lead Detail Panel' }
            ]
          }
        ]
      },
      {
        id: 'bulk-re-evaluation',
        title: 'Re-evaluating Calls',
        roles: ADMIN_ONLY,
        keywords: ['re-evaluate', 'bulk', 'recategorize', 'admin'],
        content: [
          {
            type: 'paragraph',
            text: 'Admins can re-evaluate calls to update scores when evaluation criteria change, or re-categorize a call under a different call type.'
          },
          {
            type: 'heading',
            text: 'Re-categorizing a Single Call'
          },
          {
            type: 'steps',
            items: [
              'Open the evaluation report',
              'In the Call Type section, click "Re-categorize & Re-evaluate"',
              'Select the new call type from the dropdown',
              'Click "Re-evaluate" — the AI will re-score the call under the new category'
            ]
          },
          {
            type: 'warning',
            text: 'Re-categorization is not available for expired evaluations. Make sure to re-evaluate calls while they are still active.'
          },
          {
            type: 'heading',
            text: 'Bulk Re-evaluation'
          },
          {
            type: 'steps',
            items: [
              'In the Evaluations tab, use the checkboxes to select multiple evaluations',
              'Click "Re-Evaluate Selected" in the toolbar',
              'Processing happens in batches of 3 — a progress bar shows status',
              'Review the results showing success/failure counts and any score changes'
            ]
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 4. SCRIPT BUILDER
  // ─────────────────────────────────────────────
  {
    id: 'script-builder',
    title: 'Script Builder',
    icon: '📝',
    roles: LEAD_AND_ADMIN,
    articles: [
      {
        id: 'script-builder-overview',
        title: 'Script Builder Overview',
        roles: LEAD_AND_ADMIN,
        keywords: ['scripts', 'builder', 'overview', 'tabs'],
        content: [
          {
            type: 'image',
            src: '/help-images/script-builder-dashboard.png',
            alt: 'Script Builder My Scripts tab showing the list of scripts with performance metrics and the four navigation tabs at the top',
            caption: 'The Script Builder — four tabs for managing, editing, creating, and improving your call scripts'
          },
          {
            type: 'paragraph',
            text: 'The Script Builder is your home for creating and managing call scripts. You can have as many scripts as you want — one for cold outreach, one for warm leads, one for each product — the possibilities are endless.'
          },
          {
            type: 'heading',
            text: 'The Four Tabs'
          },
          {
            type: 'list',
            items: [
              '📋 My Scripts — See all your scripts at a glance, with performance stats like calls made, leads generated, and conversion rate. This is your starting point.',
              '✏️ Edit Script — The full script editor. Click any node to change what agents say, add response options, write coaching tips, and more.',
              '🤖 AI Script Creator — Let AI generate a brand-new script from your actual recorded calls. The more calls you\'ve uploaded, the better the result.',
              '✨ AI Improve — Pick an existing script and get AI-powered suggestions to make it better, based on real call data.'
            ]
          },
          {
            type: 'tip',
            text: 'New to Script Builder? Start by browsing the Marketplace for a ready-made script, then customize it to fit your team. Click "+ Create New Script" → "Browse Marketplace" to explore.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'my-scripts-list', title: 'My Scripts Dashboard' },
              { sectionId: 'script-builder', articleId: 'editing-scripts', title: 'Editing Scripts (Node Editor)' },
              { sectionId: 'script-builder', articleId: 'flowchart-editor', title: 'Using the Flowchart Editor' }
            ]
          }
        ]
      },
      {
        id: 'my-scripts-list',
        title: 'My Scripts Dashboard',
        roles: LEAD_AND_ADMIN,
        keywords: ['scripts', 'list', 'dashboard', 'performance', 'metrics', 'create'],
        content: [
          {
            type: 'paragraph',
            text: 'The My Scripts tab is your script library. It shows every script you have, how they\'re performing, and gives you easy access to create new ones. You can have as many scripts as you want — there\'s no limit!'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-dashboard.png',
            alt: 'My Scripts tab showing Scripts Performance table with script names, calls, leads, conversion rates, and action buttons',
            caption: 'My Scripts — your script library with live performance data for every script'
          },
          {
            type: 'heading',
            text: 'Reading the Performance Table'
          },
          {
            type: 'list',
            items: [
              'Script Name — A ★ star marks your team\'s default script (the one agents see first)',
              'Calls — How many calls have been made using this script',
              'Leads — How many leads were captured (shown in green when greater than zero)',
              'Rate — The conversion rate: what percentage of calls ended in a lead',
              'Drop-offs — Which question causes the most call abandonments (great for spotting weak spots)',
              'Actions — Click "Edit" to open the script in the Flowchart Editor, or the gear icon for Advanced Settings'
            ]
          },
          {
            type: 'heading',
            text: 'Changing the Time Period'
          },
          {
            type: 'paragraph',
            text: 'Use the dropdown in the top-right to view performance for Today, Last 7 Days, Last 30 Days, or All Time. This helps you see if a script is improving over time.'
          },
          {
            type: 'heading',
            text: 'Creating a New Script'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-create-menu.png',
            alt: 'Create New Script dropdown showing three options: Build from Scratch, Generate with AI, and Browse Marketplace',
            caption: 'Three ways to create a script — from scratch, with AI, or from the Marketplace'
          },
          {
            type: 'list',
            items: [
              'Click the blue "+ Create New Script" button at the top',
              '✏️ Build from Scratch — Enter a name and start adding nodes manually. Best for teams who know exactly what they want.',
              '🤖 Generate with AI — Let AI build a script from your recorded calls. Requires at least 3 completed evaluations.',
              '🛒 Browse Marketplace — Choose from ready-made scripts by other sales teams. The fastest way to get started!'
            ]
          },
          {
            type: 'heading',
            text: 'Script Detail Panel'
          },
          {
            type: 'paragraph',
            text: 'Click anywhere on a script row to open a quick summary panel on the right. It shows you the total number of questions, responses, and objections in the script, a preview of the opening line, and more — handy for reviewing a script before opening it.'
          },
          {
            type: 'tip',
            text: 'Look at your scripts with the highest drop-off rates — those are your best improvement opportunities. A script with a 5% lead rate might double to 10% with just a few small tweaks.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'editing-scripts', title: 'Editing Scripts (Node Editor)' },
              { sectionId: 'script-builder', articleId: 'flowchart-editor', title: 'Using the Flowchart Editor' },
              { sectionId: 'marketplace', articleId: 'browsing-scripts', title: 'Browsing Marketplace Scripts' },
              { sectionId: 'script-builder', articleId: 'ai-script-creator', title: 'AI Script Creator' }
            ]
          }
        ]
      },
      {
        id: 'editing-scripts',
        title: 'Editing Scripts (Node Editor)',
        roles: LEAD_AND_ADMIN,
        keywords: ['edit', 'node', 'editor', 'response', 'build', 'question', 'text input'],
        content: [
          {
            type: 'paragraph',
            text: 'Every script is made up of "nodes" — think of each node as one question or statement in the conversation. The Edit Script tab lets you click into any node and customize exactly what your agents say, how prospects can respond, and what happens next.'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-edit-tab.png',
            alt: 'Edit Script tab showing the node list on the left with nodes sorted by phase, and the toolbar at the top',
            caption: 'The Edit Script tab — all your script\'s nodes listed on the left, click any to edit it'
          },
          {
            type: 'heading',
            text: 'The Toolbar at the Top'
          },
          {
            type: 'list',
            items: [
              'Script selector dropdown — Switch between your scripts without leaving the editor',
              'Auto-save toggle — When on (green), changes are saved automatically every few seconds',
              'Set Default button — Makes this script the one agents see when they open the call screen',
              'More actions (⋯) — Save as New Script (make a copy), Delete Script',
              'Validation dot — 🟢 Green means everything looks good; 🟡 Yellow means there are issues worth checking'
            ]
          },
          {
            type: 'heading',
            text: 'The Node List (Left Side)'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-nodes-tab.png',
            alt: 'Nodes subtab in the Script Builder showing a list of script nodes with color-coded status dots, phase labels, and response counts',
            caption: 'The Nodes tab — every node (question/statement) in your script listed with status dots and phase labels',
          },
          {
            type: 'list',
            items: [
              'Every question/statement in your script appears here as a card',
              'Each card shows the node\'s ID, what phase it belongs to, a preview of the script text, and how many response options it has',
              'Color-coded dots: 🟢 Green = starting node, 🔴 Red = incomplete (missing script text or responses), 🟡 Yellow = unreachable (no way to get here from the start)',
              'Use the search box to quickly find a specific node',
              'Click any node card to open it in the editor on the right'
            ]
          },
          {
            type: 'heading',
            text: 'The Node Editor (Right Side)'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-node-editor.png',
            alt: 'Node editor panel showing Question ID field, Opening Node checkbox, What You\'ll Say text area, Tips & Coaching Notes area, and the Responses section',
            caption: 'Click any node to edit it — set the script text, coaching tips, and response options'
          },
          {
            type: 'list',
            items: [
              'Question ID — A short unique name for this node (e.g. "opening", "qualify_budget"). Used for tracking and analytics.',
              'Mark as Opening Node checkbox — Only one node can be the "opening" node — it\'s where every call starts',
              'What You\'ll Say — The actual script text that appears in the blue box during a call. This is what agents read to the prospect.',
              'Tips & Coaching Notes — Helpful hints and reminders that appear in the yellow coaching box. Great for objection tips, tone guidance, or key points to mention.',
              'Phase — Assign this node to a phase (e.g. "Introduction", "Qualifying", "Closing") to keep your script organized'
            ]
          },
          {
            type: 'heading',
            text: 'Setting Up Responses'
          },
          {
            type: 'list',
            items: [
              'Each node needs at least one response so the call can move forward',
              'Button response — The prospect picks from a list of options (e.g. "Yes, interested" / "Not right now")',
              'Text input response — The prospect gives a free-form answer (e.g. typing their budget, address, or name)',
              'For each response, choose what happens next: go to an existing node, create a new node, or end the script',
              'Use "End Script" when the call is complete — it prompts agents to save the lead information'
            ]
          },
          {
            type: 'tip',
            text: 'The "What You\'ll Say" text becomes the blue Agent Script box during a real call, and "Tips & Coaching Notes" becomes the yellow coaching box at the bottom. Write coaching notes as if you\'re whispering advice to your agent mid-call!'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'flowchart-editor', title: 'Using the Flowchart Editor' },
              { sectionId: 'script-builder', articleId: 'phases-and-objections', title: 'Phases & Objections' },
              { sectionId: 'call-script', articleId: 'following-the-script', title: 'Following the Script (Agent View)' },
              { sectionId: 'call-script', articleId: 'coaching-notes', title: 'Coaching Notes (Agent View)' }
            ]
          }
        ]
      },
      {
        id: 'script-validation',
        title: 'Script Validation',
        roles: LEAD_AND_ADMIN,
        keywords: ['validation', 'errors', 'warnings', 'incomplete', 'broken'],
        content: [
          {
            type: 'image',
            src: '/help-images/script-builder-validation.png',
            alt: 'Script Builder Edit tab showing the yellow Needs Attention validation banner expanded, with a list of script issues below it',
            caption: 'The validation panel — click "Needs Attention" to expand it and see exactly what needs to be fixed'
          },
          {
            type: 'paragraph',
            text: 'The script editor automatically checks your script for problems every time you make a change. The validation indicator sits just below the toolbar — it tells you at a glance whether your script is ready to use.'
          },
          {
            type: 'heading',
            text: 'Validation Checks'
          },
          {
            type: 'list',
            items: [
              'Green — Script is valid with no issues',
              'Yellow (Needs Attention) — Script has warnings. Click the banner to expand the list of issues.',
              'Each issue is described in plain language so you know exactly what to fix'
            ]
          },
          {
            type: 'heading',
            text: 'What\'s Checked — and What It Means'
          },
          {
            type: 'paragraph',
            text: 'Click the validation banner to expand the details panel and see a list of all issues:'
          },
          {
            type: 'list',
            items: [
              '"No opening node" — Your script doesn\'t have a starting point. Set one node as the opening node in the node editor.',
              '"Node has no responses" — This node is a dead end. Add at least one response button so agents can proceed from this point.',
              '"Node missing script text" — This node has no words for the agent to read. Add script text in the node editor.',
              '"Broken connection" — A response button points to a node that was deleted. Either delete the broken response or re-connect it to an existing node.',
              '"Unreachable node" — This node exists but no other node links to it. Connect it from another node\'s response, or delete it if it\'s no longer needed.',
              '"No path to end" — There\'s no route through the script that reaches an End Script node. Check that at least one response eventually leads to an End node.'
            ]
          },
          {
            type: 'warning',
            text: 'Scripts with validation errors may not work correctly during calls. Always resolve all issues before assigning the script to your team.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'revision-history', title: 'Script Revision History' },
              { sectionId: 'marketplace', articleId: 'selling-scripts', title: 'Listing a Script for Sale' }
            ]
          }
        ]
      },
      {
        id: 'phases-and-objections',
        title: 'Phases & Objections',
        roles: LEAD_AND_ADMIN,
        keywords: ['phases', 'objections', 'organize', 'structure', 'manage'],
        content: [
          {
            type: 'paragraph',
            text: 'Phases and Objections are two powerful tools that help you organize your script and prepare agents for pushback. Find them as sub-tabs inside the Edit Script tab.'
          },
          {
            type: 'heading',
            text: 'What Are Phases?'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-phases.png',
            alt: 'Phases subtab showing phase cards like Introduction, Discovery, Objection Handling, and Closing with node counts and reorder handles',
            caption: 'The Phases tab — organize your script into chapters like Introduction, Discovery, and Closing',
          },
          {
            type: 'paragraph',
            text: 'Phases are like chapters in your script. For example: "Introduction → Discovery → Objection Handling → Closing". Every node can be assigned to a phase, which helps agents know where they are in the conversation and helps you spot drop-off trends by phase in the Command Center.'
          },
          {
            type: 'list',
            items: [
              'Click the "Phases" sub-tab in the Edit Script tab to manage them',
              'Click "+ Add Phase" to create a new one',
              'Drag phases up and down to reorder them',
              'Click the pencil icon to rename a phase',
              'Each phase card shows how many nodes are assigned to it',
              'Delete a phase with the red X (you must reassign its nodes first)',
              'Assign a node to a phase by editing the node and choosing from the Phase dropdown'
            ]
          },
          {
            type: 'heading',
            text: 'What Are Objections?'
          },
          {
            type: 'paragraph',
            text: 'Objections are pre-written responses to common prospect pushback like "I\'m not interested" or "I already have an agent". You write the objection once, and agents see it as a red button they can tap at any point during that phase.'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-objections.png',
            alt: 'Objections sub-tab showing objection cards grouped by phase with label and response text for each',
            caption: 'The Objections tab — write objection handling scripts once, use them across the entire phase',
          },
          {
            type: 'list',
            items: [
              'Click the "Objections" sub-tab to see all objections grouped by phase',
              'Click "+ Add Objection" under any phase',
              'Label — What the prospect says (e.g. "I\'m already working with an agent")',
              'Response — What the agent should say back (e.g. "That\'s great! We actually work alongside buyer\'s agents...")',
              'Agents see objection buttons as red pills below the main script buttons during a call',
              'Only the objections for the current phase are shown — keeping it simple and relevant'
            ]
          },
          {
            type: 'note',
            text: 'During calls, objection buttons appear in red below the regular response options. Agents only see objections for the phase they\'re currently in — so they\'re never overwhelmed with too many options.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'editing-scripts', title: 'Editing Scripts (Node Editor)' },
              { sectionId: 'call-script', articleId: 'objection-handling', title: 'Handling Objections (Agent View)' }
            ]
          }
        ]
      },
      {
        id: 'flowchart-editor',
        title: 'Using the Flowchart Editor',
        roles: LEAD_AND_ADMIN,
        keywords: ['flowchart', 'visual', 'drag', 'drop', 'editor', 'diagram', 'node', 'rename', 'auto layout'],
        content: [
          {
            type: 'paragraph',
            text: 'The Flowchart Editor shows your entire script as a visual map — boxes connected by arrows. It\'s the easiest way to see the full call flow at once and make structural changes. Think of it as a bird\'s-eye view of your script.'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-flowchart.png',
            alt: 'Flowchart Editor showing script nodes as boxes connected by arrows, with a toolbar at the top for Save, Auto Layout, and other actions',
            caption: 'The Flowchart Editor — see your entire script as a connected visual map'
          },
          {
            type: 'heading',
            text: 'How to Open the Flowchart Editor'
          },
          {
            type: 'list',
            items: [
              'Go to My Scripts tab and click "Edit" on any script row — this opens it directly in the Flowchart Editor',
              'You can also access it from the Edit Script tab via the "Flowchart" button in the toolbar'
            ]
          },
          {
            type: 'heading',
            text: 'Navigating the Canvas'
          },
          {
            type: 'list',
            items: [
              'Scroll to zoom in and out, or use the zoom controls in the bottom corner',
              'Click and drag the background to pan around the canvas',
              'Use the minimap (bottom-right) to see where you are in the full diagram',
              'Click the "Fit View" button to zoom out and see everything at once'
            ]
          },
          {
            type: 'heading',
            text: 'Editing a Node'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-flowchart-node-edit.png',
            alt: 'Node editing in the Flowchart Editor with the node\'s script text, coaching notes, and responses visible in an inline editor',
            caption: 'Double-click any node to edit its script text, coaching notes, and responses right in the flowchart'
          },
          {
            type: 'list',
            items: [
              'Double-click any node to open its editor — change the script text, coaching notes, phase, and more',
              'Single-click a node to select it and see a quick summary',
              'Drag nodes around the canvas to rearrange them however you like',
              'The arrows between nodes show how each response connects to the next question',
              'The opening node (where the call starts) is always shown at the top'
            ]
          },
          {
            type: 'heading',
            text: 'Adding and Removing Nodes'
          },
          {
            type: 'list',
            items: [
              'Double-click on any empty space on the canvas to create a brand-new node at that spot',
              'Right-click a node for a menu with options including Delete',
              'Use the "Add Node" button in the toolbar to add without placing it manually'
            ]
          },
          {
            type: 'heading',
            text: 'Auto Layout'
          },
          {
            type: 'paragraph',
            text: 'Click "Auto Layout" in the toolbar to automatically arrange all your nodes in a clean top-to-bottom flow. The opening node is always placed at the very top. This is great after adding or moving lots of nodes.'
          },
          {
            type: 'heading',
            text: 'Renaming a Node ID'
          },
          {
            type: 'paragraph',
            text: 'You can rename a node\'s ID directly in the flowchart by clicking the node\'s ID label. Important: if a node ID has analytics history (call drop-off data), changing it will break that historical tracking. A warning will appear listing which metrics will be affected before you confirm.'
          },
          {
            type: 'heading',
            text: 'Saving Your Changes'
          },
          {
            type: 'paragraph',
            text: 'Click "Save" in the toolbar to save your layout and script changes. Click "Discard" to undo everything since your last save. If you try to leave without saving, you\'ll be asked whether to save or discard.'
          },
          {
            type: 'tip',
            text: 'Use the Flowchart Editor to plan the overall structure first, then switch to the Edit Script tab for detailed work on each node\'s script text and responses.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'editing-scripts', title: 'Editing Scripts (Node Editor)' },
              { sectionId: 'script-builder', articleId: 'phases-and-objections', title: 'Phases & Objections' },
              { sectionId: 'script-builder', articleId: 'script-validation', title: 'Script Validation' }
            ]
          }
        ]
      },
      {
        id: 'ai-script-creator',
        title: 'AI Script Creator',
        roles: LEAD_AND_ADMIN,
        keywords: ['ai', 'generate', 'automatic', 'smart', 'create', 'calls'],
        content: [
          {
            type: 'paragraph',
            text: 'The AI Script Creator is like having a script coach analyze all your best calls and write you a new script based on what actually works. It reads your evaluated calls and builds a structured script around the language and approaches that led to the best outcomes.'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-ai-creator.png',
            alt: 'AI Script Creator tab showing a list of evaluated calls to select from, with generation options and a Generate button',
            caption: 'AI Script Creator — select your best calls and let AI build you a script'
          },
          {
            type: 'heading',
            text: 'Before You Start'
          },
          {
            type: 'note',
            text: 'You need at least 3 completed call evaluations to generate a script. More calls = better results. If you\'re just starting out, upload a few calls through the AI Call Evaluator first.'
          },
          {
            type: 'heading',
            text: 'Generating a Script — Step by Step'
          },
          {
            type: 'steps',
            items: [
              'Go to Script Builder → AI Script Creator tab',
              'You\'ll see a list of your evaluated calls — check the boxes to select the ones you want to use (the more the better!)',
              'Optionally adjust the generation settings: script tone, focus areas, or call type',
              'Click "Generate Script" — the AI will analyze your calls and build the script (this takes 1–3 minutes)',
              'Review the generated script preview organized into sections like Opening, Discovery, Objection Handling, and Closing'
            ]
          },
          {
            type: 'heading',
            text: 'What to Do with the Generated Script'
          },
          {
            type: 'list',
            items: [
              'Give it a name in the template name field at the top',
              'Click "Edit Script" to open it in the Script Builder and customize it before saving',
              'Click "Regenerate" to try again with the same call selection (useful if the first result isn\'t quite right)',
              'Click "Start Fresh" to go back and pick different calls',
              'Click "Save as Template" to add it to your team\'s script library right away'
            ]
          },
          {
            type: 'heading',
            text: 'Generation History'
          },
          {
            type: 'paragraph',
            text: 'Every script you\'ve generated is saved in the "Generation History" tab. You can go back and view or save any previous generation — nothing is ever lost.'
          },
          {
            type: 'tip',
            text: 'Try selecting only your highest-scoring evaluations for the best results. A script built from 5 excellent calls will outperform one built from 20 mediocre ones.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call for Evaluation' },
              { sectionId: 'script-builder', articleId: 'my-scripts-list', title: 'My Scripts Dashboard' },
              { sectionId: 'script-builder', articleId: 'ai-improvements', title: 'AI Script Improvements' }
            ]
          }
        ]
      },
      {
        id: 'ai-improvements',
        title: 'AI Script Improvements',
        roles: LEAD_AND_ADMIN,
        keywords: ['ai', 'improve', 'suggestions', 'improvements', 'optimize', 'enhance', 'get improvements'],
        content: [
          {
            type: 'paragraph',
            text: 'The AI Improve tab is like a performance coach for your scripts. Instead of building a new script from scratch, it takes your existing script and tells you exactly what to change — and why — based on how your actual calls have been going.'
          },
          {
            type: 'image',
            src: '/help-images/script-builder-ai-improvements.png',
            alt: 'AI Improvements tab showing a script selector, a "Get AI Improvements" button, and a list of improvement suggestions with Apply buttons',
            caption: 'AI Improve — pick a script, click Get Improvements, and see exactly what to change'
          },
          {
            type: 'heading',
            text: 'Before You Start'
          },
          {
            type: 'note',
            text: 'You need at least 5 completed call evaluations for the AI to make meaningful suggestions. The more evaluations you have, the more accurate and specific the suggestions will be.'
          },
          {
            type: 'heading',
            text: 'How to Get Improvement Suggestions'
          },
          {
            type: 'steps',
            items: [
              'Go to Script Builder → AI Improve tab',
              'Select the script you want to improve from the dropdown',
              'Click "Get AI Improvements" — the AI analyzes your recent call evaluations against your script',
              'A list of suggestions appears — each one explains what to change and why',
              'Click "Apply" on any suggestion you like — the change is made to your script instantly',
              'Skip or dismiss suggestions that don\'t feel right for your team'
            ]
          },
          {
            type: 'heading',
            text: 'What the AI Looks For'
          },
          {
            type: 'list',
            items: [
              'Parts of the script that consistently score low across multiple evaluations',
              'Questions where prospects frequently drop off or disengage',
              'Opening and closing language that correlates with more leads being captured',
              'Missing objection responses that come up repeatedly in calls',
              'Transition phrases that make conversations flow more naturally'
            ]
          },
          {
            type: 'tip',
            text: 'Run AI Improvements once a month as you accumulate more call data. The suggestions get smarter over time — a script that\'s been improved 3 times will perform much better than one that\'s never been touched.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'ai-script-creator', title: 'AI Script Creator' },
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call for Evaluation' },
              { sectionId: 'script-builder', articleId: 'editing-scripts', title: 'Editing Scripts (Node Editor)' }
            ]
          }
        ]
      },
      {
        id: 'revision-history',
        title: 'Script Revision History',
        roles: LEAD_AND_ADMIN,
        keywords: ['revision', 'history', 'version', 'version history', 'revision history', 'restore', 'undo', 'rollback', 'previous version', 'changes'],
        content: [
          {
            type: 'image',
            src: '/help-images/script-builder-history.png',
            alt: 'Script Builder Edit tab with the Revision History panel open on the right, showing a list of past versions with dates and restore buttons',
            caption: 'Revision History — click the History button in the toolbar to see all past versions of your script'
          },
          {
            type: 'paragraph',
            text: 'Script Builder automatically saves a revision history for every script. You can view past versions and restore a previous version if needed.'
          },
          {
            type: 'heading',
            text: 'Accessing Revision History'
          },
          {
            type: 'steps',
            items: [
              'Open the script you want to review in Script Builder',
              'Look for the "History" or clock icon in the toolbar',
              'Click it to open the Revision History panel on the right',
              'Browse the list of revisions — each shows the date, time, and who made the change',
              'Click a revision to preview what the script looked like at that point',
              'Click "Restore" to roll back the script to that version'
            ]
          },
          {
            type: 'heading',
            text: 'What Restoring Does'
          },
          {
            type: 'paragraph',
            text: 'Clicking "Restore" replaces the current version of your script with the selected older version. A new revision entry is created automatically so you can always come back to the version you had before restoring.'
          },
          {
            type: 'list',
            items: [
              'The restored version becomes the current live script immediately',
              'A new revision entry is added at the top of the list with a "Restored from [date]" label',
              'Your agents will see the restored version the next time they open the Call Script page',
              'You can undo a restore by opening Revision History again and restoring the previous version'
            ]
          },
          {
            type: 'heading',
            text: 'How Many Revisions Are Kept?'
          },
          {
            type: 'paragraph',
            text: 'The system automatically keeps the most recent revisions of your script. Older revisions may be pruned over time to keep storage manageable. There is no fixed time limit, but it\'s good practice to restore quickly if you need an older version.'
          },
          {
            type: 'tip',
            text: 'Restoring a revision creates a new revision entry, so you can always undo a restore by selecting the previous version.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'script-builder-overview', title: 'Script Builder Overview' },
              { sectionId: 'script-builder', articleId: 'script-validation', title: 'Script Validation' }
            ]
          }
        ]
      },
    ]
  },

  // ─────────────────────────────────────────────
  // 5. DASHBOARD (Agents only)
  // ─────────────────────────────────────────────
  {
    id: 'dashboard',
    title: 'Dashboard',
    icon: '📊',
    roles: ['basic'],
    articles: [
      {
        id: 'agent-dashboard-overview',
        title: 'My Dashboard',
        roles: ['basic'],
        keywords: ['dashboard', 'my dashboard', 'performance', 'stats', 'kpis', 'calls', 'leads', 'conversion', 'evaluations', 'drop-off'],
        content: [
          {
            type: 'image',
            src: '/help-images/agent-dashboard.png',
            alt: 'Dashboard showing KPI cards for Calls, Leads, Conversion Rate, and Average Duration, plus a leaderboard and performance charts',
            caption: 'Your Dashboard — see your personal stats, where you rank on the leaderboard, and how your script is performing'
          },
          {
            type: 'paragraph',
            text: 'Your Dashboard gives you a personal snapshot of your calling performance. Find it by clicking "Dashboard" in the Management section of the sidebar.'
          },
          {
            type: 'heading',
            text: 'Your Performance Stats'
          },
          {
            type: 'paragraph',
            text: 'At the top of the Dashboard you\'ll see four KPI cards showing your numbers for the selected time period:'
          },
          {
            type: 'list',
            items: [
              '📞 My Calls — total number of calls you\'ve started',
              '🎯 My Leads — total leads you\'ve captured',
              '📈 My Conversion — percentage of calls that resulted in a lead',
              '⏱️ Avg Duration — your average call length in minutes'
            ]
          },
          {
            type: 'heading',
            text: 'Changing the Time Period'
          },
          {
            type: 'paragraph',
            text: 'Use the period selector in the top-right corner to change the date range. Options include Today, Yesterday, Last 7 Days, Last 30 Days, Last 90 Days, This Month, Last Month, and All Time. Your KPIs update instantly when you switch periods.'
          },
          {
            type: 'heading',
            text: 'My Recent Evaluations'
          },
          {
            type: 'paragraph',
            text: 'The lower section shows your five most recent AI call evaluations — including the prospect name, your score (out of 100), and the date. Click "View all" to go to the full Call Evaluator page.'
          },
          {
            type: 'heading',
            text: 'Script Drop-Off Analysis'
          },
          {
            type: 'paragraph',
            text: 'The Script Drop-Off panel shows which steps in your call scripts cause the most call endings. Use this to identify where prospects drop off so you can focus your practice on those sections.'
          },
          {
            type: 'tip',
            text: 'If you haven\'t made many calls yet, your Dashboard will show zeros. Start making calls and saving leads — your stats will update automatically after each session.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'evaluator-overview', title: 'Call Evaluator Overview' },
              { sectionId: 'troubleshooting', articleId: 'calls-not-in-analytics', title: 'My Calls Aren\'t Showing in Analytics' }
            ]
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 6. COMMAND CENTER (Team Leads only)
  // ─────────────────────────────────────────────
  {
    id: 'command-center',
    title: 'Command Center',
    icon: '📊',
    roles: LEAD_AND_ADMIN,
    articles: [
      {
        id: 'command-center-overview',
        title: 'Command Center Overview',
        roles: LEAD_AND_ADMIN,
        keywords: ['command center', 'dashboard', 'kpis', 'agent pulse', 'leaderboard', 'peak hours', 'script health', 'drop-off', 'trend', 'team performance'],
        content: [
          {
            type: 'paragraph',
            text: 'The Command Center is your team performance hub. It gives you a real-time overview of your team\'s activity — leads generated, calls made, conversion rates, agent rankings, script health, and more. Find it under Management in the sidebar.'
          },
          {
            type: 'image',
            src: '/help-images/command-center.png',
            alt: 'Command Center showing KPI cards for Leads Generated, Calls Made, Conversion Rate, and Eval Score, plus the daily trend chart',
            caption: 'The Command Center — your full team performance dashboard at a glance'
          },
          {
            type: 'heading',
            text: 'KPI Cards (Top Section)'
          },
          {
            type: 'paragraph',
            text: 'The four cards at the top show your team\'s numbers for the selected time period:'
          },
          {
            type: 'list',
            items: [
              '🎯 Leads Generated — total leads captured by your whole team',
              '📞 Calls Made — total call sessions started by all agents',
              '📈 Conversion Rate — percentage of calls that turned into a lead',
              '⭐ Eval Score — average AI evaluation score across all graded calls'
            ]
          },
          {
            type: 'heading',
            text: 'Trend Chart'
          },
          {
            type: 'paragraph',
            text: 'The chart next to the KPIs shows daily calls and leads over the selected period. Great for spotting slow days, seeing if a new script is working, or tracking the impact of a team training session.'
          },
          {
            type: 'heading',
            text: 'Calls Breakdown'
          },
          {
            type: 'paragraph',
            text: 'The funnel shows exactly how calls are progressing: sessions started → calls completed → leads captured → calls dropped. You can see at a glance where your team is losing potential leads in the process.'
          },
          {
            type: 'heading',
            text: 'Agent Pulse'
          },
          {
            type: 'image',
            src: '/help-images/command-center-agent-pulse.png',
            alt: 'Agent Pulse leaderboard showing agents ranked by leads, calls, conversion rate, and evaluation score',
            caption: 'Agent Pulse — who\'s making the most calls, generating the most leads, and scoring the highest'
          },
          {
            type: 'paragraph',
            text: 'Agent Pulse is a leaderboard that ranks your agents by their output — calls made, leads generated, and conversion rate. If an agent also has AI call evaluations, their average score appears as a badge in the Score column.'
          },
          {
            type: 'note',
            text: 'Agent Pulse and Call Evaluator Team Performance show two different things on purpose. Agent Pulse answers "who is doing the most work?" (activity and output). Call Evaluator Performance answers "how well are they doing the work?" (call quality scores). Use both together for a complete picture.'
          },
          {
            type: 'heading',
            text: 'Script Health'
          },
          {
            type: 'paragraph',
            text: 'Script Health shows which of your scripts are driving results. For each script you\'ll see how many calls used it, how many leads it produced, and its conversion rate. Low-converting scripts are highlighted so you can prioritize improvements.'
          },
          {
            type: 'heading',
            text: 'Script Drop-Off Analysis'
          },
          {
            type: 'image',
            src: '/help-images/command-center-dropoff.png',
            alt: 'Script Drop-Off Analysis showing nodes ranked by how often calls end at that point, with Node ID and script text',
            caption: 'Drop-Off Analysis — see exactly where calls are being lost in your scripts'
          },
          {
            type: 'paragraph',
            text: 'Drop-off analysis shows which steps in your scripts most often cause a call to end without capturing a lead. Each row shows the Node ID, the script text for that step, and how many times the call ended there. Use this to prioritize which parts of your script to improve first.'
          },
          {
            type: 'heading',
            text: 'Peak Calling Hours'
          },
          {
            type: 'paragraph',
            text: 'The Peak Hours chart shows when your team makes the most calls and generates the most leads by hour of day. Use this to schedule your team\'s calling blocks during the hours that historically produce the best results.'
          },
          {
            type: 'heading',
            text: 'Changing the Time Period'
          },
          {
            type: 'paragraph',
            text: 'Use the period selector in the top-right to adjust the date range for all panels at once. All charts, KPIs, leaderboards, and tables update instantly.'
          },
          {
            type: 'tip',
            text: 'Start with "Last 30 Days" for a balanced view, then drill into "Today" or "This Week" for more focused coaching conversations with individual agents.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'team-performance-analytics', title: 'Team Performance Analytics' },
              { sectionId: 'troubleshooting', articleId: 'calls-not-in-analytics', title: 'My Calls Aren\'t Showing in Analytics' }
            ]
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 7. LEADS
  // ─────────────────────────────────────────────
  {
    id: 'leads',
    title: 'Leads',
    icon: '📋',
    roles: LEAD_AND_ADMIN,
    articles: [
      {
        id: 'leads-overview',
        title: 'Leads Page Overview',
        roles: LEAD_AND_ADMIN,
        keywords: ['leads', 'overview', 'table', 'view'],
        content: [
          {
            type: 'paragraph',
            text: 'The Leads page is where every lead your team captures ends up. Every time an agent saves a lead after a call, it appears here — with all the information they collected, the agent who captured it, and the script they were using.'
          },
          {
            type: 'image',
            src: '/help-images/leads-page.png',
            alt: 'Leads page showing the time period selector, search bar, leads table with columns for Date, Agent, Script, and prospect details',
            caption: 'The Leads page — every lead your team captures, all in one place'
          },
          {
            type: 'heading',
            text: 'What You\'ll See'
          },
          {
            type: 'list',
            items: [
              'Time period selector (top-right) — Filter to see leads from Today, This Week, Last Month, All Time, etc.',
              'Search bar — Type any name, phone number, email, or field value to instantly find a lead',
              'Outcome and Temperature filters — Narrow down by call outcome or lead quality',
              'Leads table — All your leads with sortable columns (click any column header to sort)',
              'Export CSV button — Download all visible leads to a spreadsheet with one click'
            ]
          },
          {
            type: 'heading',
            text: 'Opening a Lead\'s Details'
          },
          {
            type: 'image',
            src: '/help-images/leads-detail-panel.png',
            alt: 'Lead detail panel showing prospect name, contact info, call transcript, notes, and the option to edit lead data',
            caption: 'Click any lead row to see the full details, including the call conversation history'
          },
          {
            type: 'paragraph',
            text: 'Click any row in the table to open the lead detail panel on the right. You\'ll see all the captured information, the conversation history from that call, and an option to edit the lead\'s data.'
          },
          {
            type: 'tip',
            text: 'Use the Export CSV button regularly to back up your leads or import them into your CRM. Every field your agents captured — name, phone, email, address, and any custom fields — is included in the export.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'saving-leads', title: 'Saving a Lead (from the Call Screen)' },
              { sectionId: 'leads', articleId: 'filtering-leads', title: 'Filtering & Searching Leads' },
              { sectionId: 'leads', articleId: 'lead-capture-forms', title: 'Configuring Lead Capture Forms' },
              { sectionId: 'troubleshooting', articleId: 'cant-save-lead', title: 'Can\'t Save a Lead' }
            ]
          }
        ]
      },
      {
        id: 'filtering-leads',
        title: 'Filtering & Searching Leads',
        roles: LEAD_AND_ADMIN,
        keywords: ['filter', 'search', 'sort', 'time', 'outcome', 'temperature'],
        content: [
          {
            type: 'image',
            src: '/help-images/leads-filter.png',
            alt: 'Leads page with the filter panel expanded, showing time period, outcome, temperature, agent, and search filters',
            caption: 'The filter panel — click "Show Filters" to narrow your leads by date range, outcome, temperature, or agent'
          },
          {
            type: 'heading',
            text: 'Time Period Filter'
          },
          {
            type: 'paragraph',
            text: 'Narrow leads by when they were captured. Options include: Today, This Week, Last Week, This Month, Last Month, This Quarter, Last Quarter, This Year, Last Year, and All Time.'
          },
          {
            type: 'heading',
            text: 'Outcome Filter'
          },
          {
            type: 'paragraph',
            text: 'Filter leads by how the call ended. The outcome is set by the agent when saving the lead:'
          },
          {
            type: 'list',
            items: [
              'Hot Lead — Prospect is very interested and ready to move forward',
              'Warm Lead — Prospect showed interest but needs more time or follow-up',
              'Cold Lead — Prospect is not interested right now but may be in the future',
              'Not Interested — Prospect clearly declined; unlikely to convert',
              'Callback — Prospect asked to be called back at a specific time',
              'Voicemail — No answer; left a voicemail message',
              'Wrong Number — Called the wrong person or disconnected number',
              'Do Not Call — Prospect asked not to be contacted again'
            ]
          },
          {
            type: 'heading',
            text: 'Temperature Filter'
          },
          {
            type: 'paragraph',
            text: 'Temperature is a quick visual summary of lead quality: Hot (🔥), Warm (🌡️), or Cold (❄️). It\'s automatically derived from the outcome selected when saving the lead and helps you quickly spot the highest-priority follow-ups.'
          },
          {
            type: 'heading',
            text: 'Search'
          },
          {
            type: 'paragraph',
            text: 'The search box filters across all lead fields instantly as you type — prospect name, phone, email, notes, and any custom fields your team uses.'
          },
          {
            type: 'heading',
            text: 'Sorting'
          },
          {
            type: 'paragraph',
            text: 'Click any column header to sort. Click again to reverse the sort direction. Arrows (▲/▼) indicate the current sort.'
          },
          {
            type: 'heading',
            text: 'Pagination'
          },
          {
            type: 'paragraph',
            text: 'Choose to display 25, 50, or 100 leads per page. Use Previous/Next buttons to navigate between pages.'
          }
        ]
      },
      {
        id: 'lead-table-columns',
        title: 'Lead Table & Columns',
        roles: LEAD_AND_ADMIN,
        keywords: ['table', 'columns', 'data', 'fields', 'drop-off', 'completion'],
        content: [
          {
            type: 'image',
            src: '/help-images/leads-page.png',
            alt: 'Leads page table showing columns for Date, Time, Agent, Script, custom fields, and Eval score',
            caption: 'The leads table — columns adjust automatically based on your lead capture form fields'
          },
          {
            type: 'heading',
            text: 'Fixed Columns (Always Visible)'
          },
          {
            type: 'list',
            items: [
              'Date — The date the lead was saved. Useful for tracking daily and weekly volume.',
              'Time — The time of day the call was completed. Helps identify your team\'s peak calling hours.',
              'Agent — The agent who saved the lead. Essential for understanding individual performance.',
              'Script — Which call script was used. Lets you compare outcomes across different scripts.',
              'Drop-off — The last node reached before saving, if the call ended early. Helps identify where agents are getting stuck or calls are dropping off most.',
              'Eval — The AI evaluation score if a recorded call has been linked to this lead. Blank if no evaluation is attached.'
            ]
          },
          {
            type: 'heading',
            text: 'Dynamic Columns (Your Custom Fields)'
          },
          {
            type: 'paragraph',
            text: 'The table automatically adds a column for each field in your lead capture form. For example, if your form collects Prospect Name, Phone Number, and Property Address, those appear as separate columns. This means the table structure is unique to your team\'s setup.'
          },
          {
            type: 'tip',
            text: 'Click any column header to sort the table by that column. Click again to reverse the order. This makes it easy to find your highest-scoring leads or see which agents are saving the most leads today.'
          }
        ]
      },
      {
        id: 'lead-detail-panel',
        title: 'Lead Detail Panel',
        roles: LEAD_AND_ADMIN,
        keywords: ['detail', 'panel', 'lead', 'information', 'conversation', 'side', 'edit'],
        content: [
          {
            type: 'image',
            src: '/help-images/leads-detail-panel.png',
            alt: 'Leads page with the detail panel open on the right, showing lead meta info, collected form data, and conversation history',
            caption: 'The Lead Detail Panel — opens on the right when you click any lead row'
          },
          {
            type: 'paragraph',
            text: 'Click any lead row in the table to open the detail panel on the right side. It gives you a complete view of everything captured during that call.'
          },
          {
            type: 'heading',
            text: 'What\'s in the Panel'
          },
          {
            type: 'list',
            items: [
              'Meta Info — Date and time, agent name, and which script was used',
              'Evaluation — The AI evaluation score if a call recording has been linked to this lead (with a button to link or unlink)',
              'Collected Data — All the form fields that were filled in when saving the lead (e.g., prospect name, phone, email)',
              'Conversation History — The full step-by-step transcript of the call, showing which script path was followed and any objections that came up'
            ]
          },
          {
            type: 'heading',
            text: 'Editing a Lead\'s Captured Data'
          },
          {
            type: 'paragraph',
            text: 'You can edit a lead\'s collected data after saving it. In the "Collected Data" section of the detail panel, look for an Edit button or click directly on a field to update it.'
          },
          {
            type: 'steps',
            items: [
              'Open the lead detail panel by clicking the lead row',
              'Scroll to the "Collected Data" section',
              'Click the Edit button (pencil icon) if one is shown, or click the field value you want to change',
              'Make your edits and click Save to update the lead record'
            ]
          },
          {
            type: 'heading',
            text: 'Closing the Panel'
          },
          {
            type: 'paragraph',
            text: 'Close the panel by clicking the X button at the top-right of the panel, or press the Escape key on your keyboard. You can also click any other lead row to switch to that lead\'s details.'
          },
          {
            type: 'tip',
            text: 'The Conversation History in the detail panel shows every step of the script the agent followed, making it easy to understand exactly what was discussed during the call without listening to a recording.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'conversation-history', title: 'Conversation History' },
              { sectionId: 'call-evaluator', articleId: 'linking-evaluations-to-leads', title: 'Linking Evaluations to Leads' }
            ]
          }
        ]
      },
      {
        id: 'exporting-leads',
        title: 'Exporting Leads',
        roles: LEAD_AND_ADMIN,
        keywords: ['export', 'csv', 'download', 'spreadsheet'],
        content: [
          {
            type: 'image',
            src: '/help-images/leads-export.png',
            alt: 'Leads page header showing the Export CSV button at the top right of the table',
            caption: 'The Export CSV button — applies your current filters, then downloads everything as a spreadsheet'
          },
          {
            type: 'paragraph',
            text: 'Export your leads as a CSV file for use in spreadsheets, CRM systems, or other tools.'
          },
          {
            type: 'heading',
            text: 'How to Export'
          },
          {
            type: 'steps',
            items: [
              'Apply any filters you want first — the export includes only the filtered results',
              'Click the "Export CSV" button in the header',
              'A CSV file downloads with all visible columns, including custom fields'
            ]
          },
          {
            type: 'tip',
            text: 'The exported filename includes the time period and date (e.g., leads-this-week-2026-02-16.csv), making it easy to organize your exports.'
          }
        ]
      },
      {
        id: 'lead-capture-forms',
        title: 'Configuring Lead Capture Forms',
        roles: LEAD_AND_ADMIN,
        keywords: ['form', 'fields', 'custom', 'lead', 'capture', 'configure', 'prospect'],
        content: [
          {
            type: 'image',
            src: '/help-images/leads-lead-capture-form.png',
            alt: 'Leads page with the Edit Lead Capture Form panel expanded, showing the default form fields (Prospect Name, Phone Number, Email Address, Property Address) and an Edit Fields button',
            caption: 'Click "Edit Lead Capture Form" on the Leads page to expand the form editor and configure which fields agents see when saving a lead'
          },
          {
            type: 'paragraph',
            text: 'Customize what information agents collect during calls. You can set a team-wide default form and, with a Business plan, create per-script custom forms. This is configured directly on the Leads page.'
          },
          {
            type: 'heading',
            text: 'Team Default Form'
          },
          {
            type: 'steps',
            items: [
              'Go to the Leads page',
              'Toggle "Edit Lead Capture Form" to expand the form editor',
              'Add, remove, or reorder fields',
              'Click "Save" to apply changes to all future calls'
            ]
          },
          {
            type: 'heading',
            text: 'Per-Script Forms (Business)'
          },
          {
            type: 'paragraph',
            text: 'With a Business subscription, you can create custom forms for individual scripts. On the Leads page, use the script selector dropdown to choose a specific script, then configure its form. You can also switch a script back to using the team default.'
          },
          {
            type: 'heading',
            text: 'Field Types'
          },
          {
            type: 'list',
            items: [
              'Text — Short text input (name, company, etc.)',
              'Phone — Phone number input',
              'Email — Email address with validation',
              'Long Text — Multi-line textarea for notes and details',
              'Each field has a label, can be marked as required or optional',
              'Drag fields to reorder them in the form'
            ]
          },
          {
            type: 'note',
            text: 'Changes to the lead capture form affect all future calls. Existing saved leads are not affected by form changes.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'saving-leads', title: 'Saving a Lead' },
              { sectionId: 'leads', articleId: 'lead-table-columns', title: 'Lead Table & Columns' }
            ]
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 6. MARKETPLACE
  // ─────────────────────────────────────────────
  {
    id: 'marketplace',
    title: 'Marketplace',
    icon: '🛒',
    roles: LEAD_AND_ADMIN,
    articles: [
      {
        id: 'marketplace-overview',
        title: 'Marketplace Overview',
        roles: LEAD_AND_ADMIN,
        keywords: ['marketplace', 'overview', 'tabs'],
        content: [
          {
            type: 'paragraph',
            text: 'The Marketplace is a library of ready-made call scripts from other sales teams. Instead of building from scratch, you can find a script for your industry, add it to your team in one click, and customize it to fit your needs. You can also sell your own scripts!'
          },
          {
            type: 'image',
            src: '/help-images/marketplace.png',
            alt: 'Script Marketplace showing script cards across industries like Real Estate, Insurance, Solar, and Healthcare, each with a preview and Get Script button',
            caption: 'The Marketplace — hundreds of scripts across dozens of industries, ready to use'
          },
          {
            type: 'heading',
            text: 'Marketplace Tabs'
          },
          {
            type: 'list',
            items: [
              '🔍 Browse Scripts — Search and filter scripts by industry, price, and rating',
              '📦 My Purchases — All the scripts you\'ve bought, ready to add to your team',
              '💰 Seller Dashboard — If you sell scripts, track your listings and revenue here',
              '➕ List Script — Create a new listing to sell one of your scripts to others'
            ]
          },
          {
            type: 'tip',
            text: 'The fastest way to get started is to browse the Marketplace for a script in your industry. Add it to your team, make it your own with a few edits, and your agents can start using it the same day.'
          }
        ]
      },
      {
        id: 'browsing-scripts',
        title: 'Browsing & Searching Scripts',
        roles: LEAD_AND_ADMIN,
        keywords: ['browse', 'marketplace', 'search', 'scripts', 'find', 'filter'],
        content: [
          {
            type: 'image',
            src: '/help-images/marketplace-search.png',
            alt: 'Marketplace page with search bar filled in, showing filtered script cards with titles, ratings, and prices',
            caption: 'Search and filter the Marketplace to find the perfect script for your team'
          },
          {
            type: 'heading',
            text: 'Search'
          },
          {
            type: 'paragraph',
            text: 'Use the search bar at the top to find scripts by title, description, or tags. Results update automatically as you type.'
          },
          {
            type: 'heading',
            text: 'Filters'
          },
          {
            type: 'list',
            items: [
              'Industry — Filter by category (e.g., Real Estate, Insurance, Solar)',
              'Price — All Prices, Free, Under $10, $10-$25, $25-$50, $50+',
              'Sort — Popular, Newest, Highest Rated, Lowest Price, Highest Price',
              'Clear Filters button resets all filters'
            ]
          },
          {
            type: 'heading',
            text: 'Featured Scripts'
          },
          {
            type: 'paragraph',
            text: 'When no filters are active, a Featured Scripts section appears at the top highlighting top-rated scripts.'
          },
          {
            type: 'paragraph',
            text: 'Click any script card to view its full listing details, reviews, and purchase options.'
          }
        ]
      },
      {
        id: 'purchasing-scripts',
        title: 'Purchasing a Script',
        roles: LEAD_AND_ADMIN,
        keywords: ['purchase', 'buy', 'payment', 'add'],
        content: [
          {
            type: 'image',
            src: '/help-images/marketplace-browse.png',
            alt: 'Marketplace script cards showing title, industry tag, rating, price, and a Purchase button',
            caption: 'Click any script card to view full details and purchase it'
          },
          {
            type: 'heading',
            text: 'How to Purchase'
          },
          {
            type: 'list',
            items: [
              'Click a script card to open the listing detail page',
              'Review the description, script preview, and buyer reviews',
              'Click "Purchase" or "Add to Team"',
              'Complete the payment process',
              'The script is automatically copied to your team\'s Script Builder library',
              'You can customize the purchased script to fit your needs'
            ]
          },
          {
            type: 'tip',
            text: 'After purchasing, the script appears in your My Scripts dashboard where you can customize it to fit your team\'s needs before assigning it to agents.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'my-scripts-list', title: 'My Scripts Dashboard' },
              { sectionId: 'marketplace', articleId: 'my-purchases', title: 'My Purchases' }
            ]
          }
        ]
      },
      {
        id: 'selling-scripts',
        title: 'Listing a Script for Sale',
        roles: LEAD_AND_ADMIN,
        keywords: ['sell', 'list', 'publish', 'create listing'],
        content: [
          {
            type: 'image',
            src: '/help-images/marketplace-sell.png',
            alt: 'List Script tab showing form fields for selecting a script, adding a title, description, industry, tags, and setting a price',
            caption: 'The List Script form — fill in the details, set your price, and submit your script for review'
          },
          {
            type: 'heading',
            text: 'Creating a Listing'
          },
          {
            type: 'steps',
            items: [
              'Go to the "List Script" tab in the Marketplace',
              'Select the script from your library — the system validates the script flow automatically',
              'Fill in listing details: Title (max 100 chars), Description (min 100 chars), Industry category, and Tags',
              'Set pricing: Free or Paid (minimum $10, shows your 70% earnings per sale)',
              'Click "Create Listing" to submit your listing for review'
            ]
          },
          {
            type: 'warning',
            text: 'Scripts with validation errors cannot be listed. Make sure your script passes validation in the Script Builder before creating a listing.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'script-validation', title: 'Script Validation' },
              { sectionId: 'marketplace', articleId: 'seller-dashboard', title: 'Seller Dashboard' }
            ]
          }
        ]
      },
      {
        id: 'seller-dashboard',
        title: 'Seller Dashboard',
        roles: LEAD_AND_ADMIN,
        keywords: ['seller', 'dashboard', 'earnings', 'sales', 'manage', 'listings'],
        content: [
          {
            type: 'image',
            src: '/help-images/marketplace-seller.png',
            alt: 'Seller Dashboard showing stats cards for Total Listings, Active, Pending Review, Total Sales, and a table of listings with status badges',
            caption: 'The Seller Dashboard — track your listings, sales, and Stripe payment setup status'
          },
          {
            type: 'heading',
            text: 'Dashboard Overview'
          },
          {
            type: 'list',
            items: [
              'Stats cards — Total Listings, Active (live), Pending Review, and Total Sales',
              'Payment Setup — Stripe Connect integration status for receiving payments',
              'Listings Table — All your listings with: script name, status badge (Active/Pending/Rejected), price, sales count, rating, and actions (View, Edit, Delete)',
              'Rejected listings show the rejection reason'
            ]
          }
        ]
      },
      {
        id: 'my-purchases',
        title: 'My Purchases',
        roles: LEAD_AND_ADMIN,
        keywords: ['purchases', 'bought', 'library', 'review'],
        content: [
          {
            type: 'image',
            src: '/help-images/marketplace-purchases.png',
            alt: 'My Purchases tab showing purchased script cards with title, purchase date, seller name, price, and buttons to open in Script Editor or leave a review',
            caption: 'My Purchases — all the scripts you\'ve bought, with quick access to edit them or leave a review'
          },
          {
            type: 'heading',
            text: 'Viewing Purchases'
          },
          {
            type: 'list',
            items: [
              'Each purchase card shows: title, purchase date, seller, and price',
              'Status indicators: "Added to your scripts" (green) or "Script removed" (orange, with re-add option)',
              'Click "Open in Script Editor" to edit a purchased script',
              'Click "Review" to leave a star rating and written review'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'my-scripts-list', title: 'My Scripts Dashboard' },
              { sectionId: 'script-builder', articleId: 'editing-scripts', title: 'Editing Scripts (Node Editor)' }
            ]
          }
        ]
      },
      {
        id: 'submitting-reviews',
        title: 'Reviewing a Purchased Script',
        roles: LEAD_AND_ADMIN,
        keywords: ['review', 'rating', 'star', 'feedback', 'rate', 'purchased', 'marketplace review', 'review listing'],
        content: [
          {
            type: 'paragraph',
            text: 'After purchasing a script, you can leave a star rating and written review to help other buyers make informed decisions.'
          },
          {
            type: 'heading',
            text: 'How to Leave a Review'
          },
          {
            type: 'steps',
            items: [
              'Go to the Marketplace and click the "My Purchases" tab',
              'Find the script you want to review and click the "Review" button on its card',
              'Select a star rating (1–5 stars)',
              'Write a short description of your experience with the script',
              'Click "Submit Review" to publish it'
            ]
          },
          {
            type: 'tip',
            text: 'You can only review scripts you have purchased. Your review appears on the listing page and helps other team leads choose the right scripts.'
          }
        ]
      },
      {
        id: 'reporting-listings',
        title: 'Reporting a Listing',
        roles: LEAD_AND_ADMIN,
        keywords: ['report', 'flag', 'inappropriate', 'spam', 'abuse', 'listing', 'report listing'],
        content: [
          {
            type: 'paragraph',
            text: 'If you come across a Marketplace listing that contains misleading information, inappropriate content, or violates community guidelines, you can flag it for review.'
          },
          {
            type: 'heading',
            text: 'How to Report a Listing'
          },
          {
            type: 'steps',
            items: [
              'Open the listing you want to report by clicking on it in Browse Scripts',
              'Click the "Report" or flag icon near the listing details',
              'Select a reason for the report (e.g., Misleading, Spam, Inappropriate content)',
              'Add any optional details to help the review team investigate',
              'Click "Submit Report"'
            ]
          },
          {
            type: 'note',
            text: 'Reports are reviewed by the TreeGenie team. Listings that violate guidelines may be removed or the seller\'s account restricted.'
          }
        ]
      },
      {
        id: 'admin-approval-queue',
        title: 'Admin Approval Queue',
        roles: ADMIN_ONLY,
        keywords: ['admin', 'approve', 'reject', 'review', 'queue', 'moderation'],
        content: [
          {
            type: 'paragraph',
            text: 'Admins review and approve Marketplace listings before they go live.'
          },
          {
            type: 'heading',
            text: 'Reviewing Listings'
          },
          {
            type: 'list',
            items: [
              'Go to the "Admin Queue" tab in the Marketplace',
              'Pending listings show full details including script preview',
              'Click "Approve" to make the listing live',
              'Click "Reject" to decline — add notes explaining what needs to change',
              'The seller is notified of the approval or rejection'
            ]
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 7. TEAM MANAGER
  // ─────────────────────────────────────────────
  {
    id: 'team-manager',
    title: 'Team Manager',
    icon: '👥',
    roles: LEAD_AND_ADMIN,
    articles: [
      {
        id: 'team-manager-overview',
        title: 'Team Manager Overview',
        roles: LEAD_AND_ADMIN,
        keywords: ['team', 'manage', 'overview', 'agent slots', 'unlimited agents'],
        content: [
          {
            type: 'paragraph',
            text: 'The Team Manager is your control room for your team. Invite new agents, assign them a script, change their role, and see the status of every team member — all from one page.'
          },
          {
            type: 'image',
            src: '/help-images/team-manager.png',
            alt: 'Team Manager showing the agent slots banner (5/25, 20 available), invite form, filter bar, and team member table with columns for name, role, template, and status',
            caption: 'Team Manager — invite, assign, and manage every member of your team'
          },
          {
            type: 'heading',
            text: 'Agent Slots'
          },
          {
            type: 'paragraph',
            text: 'A banner just below the page header shows your current agent capacity — for example "Agent Slots: 5/25 (20 available)". It turns yellow when only 1 slot remains, and red when you\'ve hit the limit. You can increase your limit by upgrading your subscription plan in Settings.'
          },
          {
            type: 'heading',
            text: 'The Team Table'
          },
          {
            type: 'list',
            items: [
              'Each row shows a team member\'s name, email, role, assigned script, and status',
              'Status can be: Active (signed in and using the app), Pending Sign Up (invited but not yet joined), or Inactive',
              'Click a role badge or script assignment directly in the table to change it instantly',
              'Use the search and filter bar to find specific members quickly'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'team-manager', articleId: 'inviting-team-members', title: 'Inviting Team Members' },
              { sectionId: 'team-manager', articleId: 'managing-team-members', title: 'Managing Team Members' },
              { sectionId: 'settings', articleId: 'subscription-billing', title: 'Subscription & Billing' },
              { sectionId: 'troubleshooting', articleId: 'invite-email-not-received', title: 'Invite Email Never Arrived' }
            ]
          }
        ]
      },
      {
        id: 'inviting-team-members',
        title: 'Inviting Team Members',
        roles: LEAD_AND_ADMIN,
        keywords: ['invite', 'add', 'member', 'user', 'email', 'join', 'invite link', 'copy invite link', 'invitation link', 'copy link'],
        content: [
          {
            type: 'paragraph',
            text: 'Inviting someone to your team takes about 30 seconds. Fill in their details, click Invite, and they\'ll get everything they need when they sign up with that email address.'
          },
          {
            type: 'image',
            src: '/help-images/team-manager-invite.png',
            alt: 'Team Manager invite form showing fields for email, first name, last name, role (Agent or Team Lead), and script assignment',
            caption: 'Invite a new team member — fill in their email, role, and script, then click Invite'
          },
          {
            type: 'heading',
            text: 'How to Invite Someone'
          },
          {
            type: 'steps',
            items: [
              'Go to Team Manager in the sidebar',
              'Fill in the invite form: Email (required), First Name, Last Name',
              'Choose their Role: Agent (for callers) or Team Lead (for managers)',
              'Optionally assign a Script — the script they\'ll use when they log in',
              'Click "Invite User to Team"',
              'They\'ll be added to your team table immediately with "Pending Sign Up" status',
              'Once they sign up with that email address, they\'re automatically added to your team with the role and script you set'
            ]
          },
          {
            type: 'tip',
            text: 'After joining, new agents will be prompted to complete their profile (first and last name) via a banner at the top of the app. Remind new hires to do this on their first login.'
          },
          {
            type: 'heading',
            text: 'Resending an Invite'
          },
          {
            type: 'paragraph',
            text: 'If someone hasn\'t signed up yet, you can copy their invitation link and send it to them directly:'
          },
          {
            type: 'list',
            items: [
              'Find the member in the team table — their status shows "Pending Sign Up"',
              'Click the "🔗 Copy Invite Link" button in the Actions column',
              'Paste the link into an email, text, or Slack message and send it to them',
              'The link takes them directly to the signup page pre-filled with your team'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'team-manager', articleId: 'managing-team-members', title: 'Managing Team Members' },
              { sectionId: 'settings', articleId: 'account-settings', title: 'Account Settings' },
              { sectionId: 'troubleshooting', articleId: 'invite-email-not-received', title: 'Invite Email Never Arrived' }
            ]
          }
        ]
      },
      {
        id: 'managing-team-members',
        title: 'Managing Team Members',
        roles: LEAD_AND_ADMIN,
        keywords: ['role', 'template', 'change', 'filter', 'table'],
        content: [
          {
            type: 'image',
            src: '/help-images/team-manager-members.png',
            alt: 'Team Manager showing the members table with columns for Name, Email, Role badge, Template assignment, Status, and action buttons',
            caption: 'The team members table — click any role or template to change it inline, or use the actions menu to deactivate or remove a member'
          },
          {
            type: 'heading',
            text: 'Filters'
          },
          {
            type: 'list',
            items: [
              'Email — Search by email address',
              'Role — Filter by role (Agent, Team Lead)',
              'Status — Filter by status (Active, Invited, Inactive)',
              'Team — Filter by team assignment',
              'Results count shows "Showing X of Y team members"'
            ]
          },
          {
            type: 'heading',
            text: 'Inline Editing'
          },
          {
            type: 'list',
            items: [
              'Click a user\'s role badge to change their role via dropdown',
              'Click a user\'s template assignment to change it via dropdown',
              'Changes take effect immediately'
            ]
          },
          {
            type: 'heading',
            text: 'User Actions'
          },
          {
            type: 'list',
            items: [
              'Remove from Team — Removes the user entirely (they\'d need to be re-invited)',
              'Deactivate — Suspends access but preserves data (can be reactivated later)',
              'Reactivate — Restores access for a deactivated user',
              'All destructive actions require confirmation'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'script-builder', articleId: 'my-scripts-list', title: 'My Scripts Dashboard' },
              { sectionId: 'team-manager', articleId: 'bulk-template-assignment', title: 'Bulk Template Assignment' },
              { sectionId: 'troubleshooting', articleId: 'wrong-or-missing-script', title: 'Call Script Shows Wrong or No Script' }
            ]
          }
        ]
      },
      {
        id: 'bulk-template-assignment',
        title: 'Bulk Template Assignment',
        roles: LEAD_AND_ADMIN,
        keywords: ['bulk', 'assign', 'template', 'multiple', 'users'],
        content: [
          {
            type: 'image',
            src: '/help-images/team-manager-bulk.png',
            alt: 'Team Manager with Bulk Template Assignment mode active, showing checkboxes next to each user and a template dropdown at the top',
            caption: 'Bulk Template Assignment — select multiple agents at once and assign them the same script in one click'
          },
          {
            type: 'heading',
            text: 'How to Bulk Assign'
          },
          {
            type: 'steps',
            items: [
              'Toggle "Bulk Template Assignment" in the Quick Actions section',
              'Checkboxes appear next to each user in the table — select the users you want',
              'Choose the script template from the dropdown',
              'Click "Assign to X Selected User(s)"',
              'All selected users are updated at once'
            ]
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 8. ADMIN PANEL
  // ─────────────────────────────────────────────
  {
    id: 'admin-panel',
    title: 'Admin Panel',
    icon: '🔐',
    roles: ADMIN_ONLY,
    articles: [
      {
        id: 'admin-panel-overview',
        title: 'Admin Panel Overview',
        roles: ADMIN_ONLY,
        keywords: ['admin', 'panel', 'overview', 'tabs'],
        content: [
          {
            type: 'paragraph',
            text: 'The Admin Panel gives admins a centralized hub for all system administration. It has four tabs:'
          },
          {
            type: 'list',
            items: [
              'Users — Manage all users across all teams with role, team, and template assignment',
              'Teams — Create and manage teams, view team members, delete teams',
              'Subscriptions — View and manage plans, usage, add-ons, and billing for all teams',
              'Admin Tools — Feedback analyzer, maintenance mode, template browser, pricing management'
            ]
          }
        ]
      },
      {
        id: 'managing-all-users',
        title: 'Managing All Users',
        roles: ADMIN_ONLY,
        keywords: ['users', 'all', 'cross-team', 'system', 'manage', 'role change', 'deactivate'],
        content: [
          {
            type: 'paragraph',
            text: 'The Users tab in the Admin Panel gives you a system-wide view of every user across all teams. Unlike Team Manager (which is limited to one team), the Admin Panel shows everything in one place.'
          },
          {
            type: 'heading',
            text: 'What You Can Do in the Users Tab'
          },
          {
            type: 'list',
            items: [
              'View all users across all teams — see their email, current role, status, team, and assigned script',
              'Filter the user list by email address, role (Agent/Team Lead/Admin), status (Active/Inactive/Invited), or team',
              'Change any user\'s role — click the role dropdown next to their name and select a new role. Changes take effect when the user next refreshes the app.',
              'Move a user to a different team — select the new team from the team dropdown in the user row',
              'Assign or change a user\'s call script template — use the template dropdown in their row',
              'Deactivate a user — removes their app access without deleting their account or data. They\'ll see an "Account deactivated" message when they try to sign in.',
              'Reactivate a deactivated user — their access is restored immediately',
              'Invite a new user directly from the admin panel using the invite form at the top of the Users tab'
            ]
          },
          {
            type: 'heading',
            text: 'When To Use This vs. Team Manager'
          },
          {
            type: 'paragraph',
            text: 'Use the Admin Panel Users tab when you need to manage users across multiple teams, change roles, or deactivate accounts. Use Team Manager when a Team Lead wants to manage just their own team\'s members and invitations.'
          },
          {
            type: 'warning',
            text: 'Changing a user\'s role from Admin to Agent immediately removes their admin access. Make sure this is intentional before saving.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'team-manager', articleId: 'managing-team-members', title: 'Managing Team Members' },
              { sectionId: 'getting-started', articleId: 'understanding-roles', title: 'Understanding Roles' }
            ]
          }
        ]
      },
      {
        id: 'managing-teams',
        title: 'Managing Teams',
        roles: ADMIN_ONLY,
        keywords: ['teams', 'create', 'delete', 'organize', 'new team', 'team setup'],
        content: [
          {
            type: 'paragraph',
            text: 'The Teams tab in the Admin Panel lets you create, view, and manage all teams on the platform. Each team is completely independent — its own scripts, leads, branding, and users.'
          },
          {
            type: 'heading',
            text: 'Creating a New Team'
          },
          {
            type: 'steps',
            items: [
              'In the Teams tab, fill in the new team form at the top of the page',
              'Enter a team name (e.g., "City Realtors")',
              'Select or enter the Team Lead\'s email address',
              'Click "Create Team" — the team is set up immediately and the Team Lead receives a notification'
            ]
          },
          {
            type: 'heading',
            text: 'What Each Team Gets'
          },
          {
            type: 'list',
            items: [
              'Their own script library (scripts are not shared between teams unless sold on the Marketplace)',
              'Their own Leads database — agents from other teams cannot see these leads',
              'Their own branding settings (logo, company name, colors)',
              'Their own set of invited users — each user belongs to exactly one team'
            ]
          },
          {
            type: 'heading',
            text: 'Viewing and Managing a Team'
          },
          {
            type: 'paragraph',
            text: 'Each team appears as a card showing the team name, the Team Lead, number of members, and the date created. Click a team card to open the team management panel where you can:'
          },
          {
            type: 'list',
            items: [
              'View all members — their name, email, role, and account status',
              'Add existing users to the team from a search dropdown',
              'Remove members from the team (they lose access to the team\'s data)',
              'See the team\'s script count and lead count at a glance'
            ]
          },
          {
            type: 'heading',
            text: 'Deleting a Team'
          },
          {
            type: 'paragraph',
            text: 'Click the delete icon on a team card. You\'ll be asked to confirm before anything is deleted.'
          },
          {
            type: 'warning',
            text: 'Deleting a team permanently removes ALL its data: scripts, leads, invitations, and user associations. This cannot be undone. Make sure you really want to delete the team before confirming.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'settings', articleId: 'team-branding', title: 'Team Branding' },
              { sectionId: 'team-manager', articleId: 'inviting-team-members', title: 'Inviting Team Members' }
            ]
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 9. SETTINGS
  // ─────────────────────────────────────────────
  {
    id: 'settings',
    title: 'Settings',
    icon: '⚙️',
    roles: ALL_ROLES,
    articles: [
      {
        id: 'account-settings',
        title: 'Account Settings',
        roles: ALL_ROLES,
        keywords: ['account', 'profile', 'name', 'email', 'sign out', 'logout'],
        content: [
          {
            type: 'paragraph',
            text: 'The Settings page is where you manage your account profile and sign out. Find it by clicking "Settings" in the sidebar.'
          },
          {
            type: 'image',
            src: '/help-images/settings-page.png',
            alt: 'Settings page showing the Profile tab with fields for First Name, Last Name, Email, Role, and Team, plus a Sign Out button',
            caption: 'Settings — update your name, see your role and team, and sign out'
          },
          {
            type: 'heading',
            text: 'Your Profile Information'
          },
          {
            type: 'list',
            items: [
              'First Name and Last Name — Your name as it appears in the app and leaderboard',
              'Email — The email address you use to log in (cannot be changed here)',
              'Role — Whether you\'re an Agent or Team Lead',
              'Team — The name of your team'
            ]
          },
          {
            type: 'heading',
            text: 'Setting Up Your Name'
          },
          {
            type: 'paragraph',
            text: 'If you haven\'t added your name yet, a blue banner will appear at the top of the app reminding you to do it. Click the banner to fill in your first and last name — this is how you appear on leaderboards and call records, so it\'s worth doing right away!'
          },
          {
            type: 'heading',
            text: 'Signing Out'
          },
          {
            type: 'paragraph',
            text: 'Click the "Sign Out" button at the bottom of the Profile tab whenever you want to log out. You\'ll be returned to the login page.'
          },
          {
            type: 'warning',
            text: 'Save any open work before signing out. Unsaved changes in Script Builder won\'t be kept.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'troubleshooting', articleId: 'forgot-password', title: 'Forgot Password' },
              { sectionId: 'troubleshooting', articleId: 'signed-out-unexpectedly', title: 'Getting Signed Out Unexpectedly' }
            ]
          }
        ]
      },
      {
        id: 'subscription-billing',
        title: 'Subscription & Billing',
        roles: LEAD_AND_ADMIN,
        keywords: ['subscription', 'billing', 'plan', 'pricing', 'payment', 'credits', 'upgrade'],
        content: [
          {
            type: 'image',
            src: '/help-images/settings-billing.png',
            alt: 'Subscription tab in Settings showing current Business plan, usage meters for evaluation credits (57/300) and agent slots (5/25), plan features list, and evaluation credit packs for purchase',
            caption: 'Your current plan summary — usage meters and extra evaluation credit packs'
          },
          {
            type: 'image',
            src: '/help-images/settings-billing-plans.png',
            alt: 'Upgrade Your Plan section showing Starter, Pro, and Business plan cards side by side with monthly pricing, agent limits, evaluation quotas, and a monthly/yearly billing toggle',
            caption: 'Plan comparison — switch between monthly and yearly billing to see pricing for each tier'
          },
          {
            type: 'paragraph',
            text: 'Manage your team\'s subscription plan, view usage, and purchase additional credits from the Subscription tab.'
          },
          {
            type: 'heading',
            text: 'Current Plan'
          },
          {
            type: 'list',
            items: [
              'Shows your current plan name (Starter, Pro, Business)',
              'Usage meters for Evaluation Credits (used/total + bonus) and Agent Slots (used/limit)',
              'Plan features list with checkmarks',
              '"Manage Billing" button opens the Stripe billing portal'
            ]
          },
          {
            type: 'heading',
            text: 'Credit Packs'
          },
          {
            type: 'paragraph',
            text: 'If you need more evaluation credits, purchase credit packs without upgrading. Choose from multiple pack sizes — credits are added to your balance immediately.'
          },
          {
            type: 'heading',
            text: 'Plan Comparison'
          },
          {
            type: 'list',
            items: [
              'Compare Starter, Pro, and Business plans side by side',
              'Toggle between monthly and yearly pricing (yearly saves one month)',
              'Each plan shows: agent limit, evaluations per month, and features',
              'Click "Upgrade" to subscribe or change plans'
            ]
          },
          {
            type: 'tip',
            text: 'Yearly billing saves one month of cost compared to monthly billing. Switch from the Plan Comparison section.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'team-manager', articleId: 'team-manager-overview', title: 'Team Manager Overview (Agent Limits)' },
              { sectionId: 'call-evaluator', articleId: 'uploading-calls', title: 'Uploading a Call (Credits)' }
            ]
          }
        ]
      },
      {
        id: 'team-branding',
        title: 'Team Branding',
        roles: LEAD_AND_ADMIN,
        keywords: ['branding', 'logo', 'colors', 'fonts', 'custom', 'url', 'white label'],
        content: [
          {
            type: 'image',
            src: '/help-images/settings-branding.png',
            alt: 'Branding tab in Settings showing Company Name field, logo upload area, font and color pickers for title and tagline, and custom team URL configuration',
            caption: 'Team Branding — customize your logo, colors, fonts, and create a custom login URL for your team'
          },
          {
            type: 'paragraph',
            text: 'Customize your team\'s appearance from the Branding tab. Full branding requires a Business subscription or the White Label add-on.'
          },
          {
            type: 'heading',
            text: 'Branding Options'
          },
          {
            type: 'list',
            items: [
              'Company Name — Appears in the sidebar header and browser tab (required)',
              'Tagline — Optional subtitle below the company name, with toggle controls for where it shows (sidebar and/or browser tab)',
              'Logo — Upload your company logo (max 2MB, recommended 128x128px square). Shows in the sidebar and on the branded login page.',
              'Title Font & Color — Choose the font family and color for your company name',
              'Tagline Font & Color — Separate font and color for the tagline'
            ]
          },
          {
            type: 'heading',
            text: 'Custom Team URL'
          },
          {
            type: 'list',
            items: [
              'Create a branded login URL like treegenie.ai/team/your-company',
              'The slug must be lowercase letters, numbers, and hyphens (3-50 characters)',
              'Click "Suggest" to auto-generate from your company name'
            ]
          },
          {
            type: 'warning',
            text: 'You can only change the URL slug once per month. Choose carefully, as your team members will use this URL to log in.'
          },
          {
            type: 'heading',
            text: 'Live Preview'
          },
          {
            type: 'paragraph',
            text: 'The right side of the page shows a live preview of your sidebar header and browser tab. Changes are not applied until you click "Save Changes." Use "Reset to Defaults" to revert all branding.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'getting-started', articleId: 'navigating-the-app', title: 'Navigating the App' }
            ]
          }
        ]
      },
      {
        id: 'admin-tools',
        title: 'Admin Tools',
        roles: ADMIN_ONLY,
        keywords: ['admin', 'tools', 'problem', 'maintenance', 'template', 'pricing'],
        content: [
          {
            type: 'paragraph',
            text: 'The Admin Tools tab provides system-wide management tools.'
          },
          {
            type: 'heading',
            text: 'Available Tools'
          },
          {
            type: 'list',
            items: [
              'Feedback Analyzer — View and investigate feedback and issues reported by users through "Send Feedback." Shows screenshots, diagnostic data, and error context.',
              'Maintenance Mode — Toggle to block non-admin access during updates. Shows current status (currently allowing/blocking access).',
              'Template Browser — Browse and export templates from all teams across the system.',
              'Pricing Manager — Edit subscription tier pricing and add-on prices. Expandable section with warning that changes affect all users.'
            ]
          },
          {
            type: 'heading',
            text: 'Team Selector (Sidebar)'
          },
          {
            type: 'paragraph',
            text: 'As an admin, a "Viewing Team" dropdown appears at the bottom of the sidebar. Switch between teams to view their scripts, leads, and data without leaving the current page.'
          }
        ]
      }
    ]
  },

  // ─────────────────────────────────────────────
  // 10. TROUBLESHOOTING
  // ─────────────────────────────────────────────
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    icon: '🔧',
    roles: ALL_ROLES,
    articles: [
      {
        id: 'connection-issues',
        title: 'Connection Issues',
        roles: ALL_ROLES,
        keywords: ['connection', 'offline', 'disconnected', 'network', 'error', 'internet', 'banner'],
        content: [
          {
            type: 'paragraph',
            text: 'If TreeGenie loses connection to the internet, you\'ll see a red or orange banner at the top of the screen that says something like "Connection lost" or "You are offline."'
          },
          {
            type: 'heading',
            text: 'Signs of a Connection Problem'
          },
          {
            type: 'list',
            items: [
              'A red or orange offline banner appears at the top of the screen',
              'Pages stop loading or spinners keep spinning without completing',
              'Saving a lead shows an error instead of a success message',
              'Data from the last few seconds may not have been recorded'
            ]
          },
          {
            type: 'heading',
            text: 'What To Do'
          },
          {
            type: 'steps',
            items: [
              'Do NOT close or refresh the browser tab while on an active call — you\'ll lose unsaved progress',
              'Check your Wi-Fi or network connection and wait a moment for it to restore',
              'Once the connection is back, the app will usually reconnect automatically and the banner will disappear',
              'If the banner stays, refresh the page (Cmd+R on Mac, Ctrl+R on Windows) — but only do this after saving any in-progress work',
              'Try a different browser (Chrome is recommended for the best reliability)',
              'If the problem keeps happening, use "Send Feedback" in the sidebar to report it'
            ]
          },
          {
            type: 'heading',
            text: 'Will In-Progress Call Data Be Lost?'
          },
          {
            type: 'paragraph',
            text: 'If you lose internet DURING a call but before clicking "Save & Start New Call," the lead data is NOT saved. When the connection is restored, you can try to complete the save. If the page reloads or freezes, you\'ll need to start the call over.'
          },
          {
            type: 'note',
            text: 'If you\'re using Safari, you may need to sign in again after closing the tab. Safari handles sessions differently from Chrome and Firefox.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'troubleshooting', articleId: 'lost-internet-mid-call', title: 'Lost Internet Mid-Call' },
              { sectionId: 'troubleshooting', articleId: 'reporting-problems', title: 'Reporting a Problem' }
            ]
          }
        ]
      },
      {
        id: 'lost-internet-mid-call',
        title: 'Lost Internet Mid-Call',
        roles: ALL_ROLES,
        keywords: ['internet', 'disconnected', 'offline', 'lost connection', 'call', 'lost progress', 'network'],
        content: [
          {
            type: 'paragraph',
            text: 'If your internet connection drops while you\'re in the middle of a call, here\'s what you need to know.'
          },
          {
            type: 'heading',
            text: 'What Happens When You Go Offline'
          },
          {
            type: 'list',
            items: [
              'The page may show a connection error banner or stop loading new content',
              'Your current position in the script (which step you\'re on) stays visible on screen',
              'Any data you\'ve already entered in the call is still in the browser\'s memory — it hasn\'t been saved yet',
              'The lead will NOT be saved automatically if you lose connection before clicking Save'
            ]
          },
          {
            type: 'heading',
            text: 'What To Do'
          },
          {
            type: 'steps',
            items: [
              'Do not close or refresh the browser tab — this will cause you to lose your current call progress',
              'Check your internet connection (Wi-Fi or cable)',
              'Wait for the connection to restore — the page may reconnect automatically',
              'Once back online, try to complete the call and save the lead as normal',
              'If the page is stuck, you can refresh — but be aware you will lose unsaved call progress and will need to start the call over'
            ]
          },
          {
            type: 'warning',
            text: 'Lead data is only saved when you click "Save & Start New Call." If you lose internet before clicking that button and then refresh the page, the lead data will be lost and you\'ll need to re-enter it.'
          },
          {
            type: 'tip',
            text: 'If you know your connection is unreliable, keep a notepad handy to jot down the prospect\'s key details during the call. That way, if the app resets, you can re-enter the information quickly.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'troubleshooting', articleId: 'connection-issues', title: 'Connection Issues' },
              { sectionId: 'troubleshooting', articleId: 'cant-save-lead', title: 'Can\'t Save a Lead' }
            ]
          }
        ]
      },
      {
        id: 'no-script-assigned',
        title: 'No Script Assigned?',
        roles: ['basic'],
        keywords: ['no script', 'script missing', 'not assigned', 'blank', 'loading', 'waiting'],
        content: [
          {
            type: 'paragraph',
            text: 'If you open the Call Script page and see a loading spinner that doesn\'t go away, or a message saying no script is available, it means your Team Lead hasn\'t assigned you a script yet.'
          },
          {
            type: 'heading',
            text: 'What You\'ll See'
          },
          {
            type: 'list',
            items: [
              'A loading message like "Loading your script template..."',
              'A blank Call Script page with no script text',
              'A message telling you to contact your Team Lead'
            ]
          },
          {
            type: 'heading',
            text: 'What To Do'
          },
          {
            type: 'steps',
            items: [
              'Contact your Team Lead and let them know you don\'t have a script assigned',
              'Your Team Lead will assign a script to your account in Team Manager',
              'Once assigned, refresh the page (press Ctrl+R on Windows or Cmd+R on Mac)',
              'Your script should now load and the Call Script page will be ready to use'
            ]
          },
          {
            type: 'note',
            text: 'This is a setup step that your Team Lead controls. You cannot assign a script to yourself — it must be done by your Team Lead from the Team Manager page.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'troubleshooting', articleId: 'wrong-or-missing-script', title: 'Call Script Shows Wrong or No Script' }
            ]
          }
        ]
      },
      {
        id: 'permission-errors',
        title: 'Permission Errors',
        roles: ALL_ROLES,
        keywords: ['permission', 'denied', 'access', 'unauthorized', 'forbidden', 'blank page'],
        content: [
          {
            type: 'paragraph',
            text: 'A permission error means you\'re trying to access a feature or page that your account doesn\'t have access to. This is normal — it just means that particular feature is restricted to a different role.'
          },
          {
            type: 'heading',
            text: 'Common Situations'
          },
          {
            type: 'list',
            items: [
              '"I see a blank page instead of Call Script" — Your account may not have a script assigned yet. Contact your Team Lead.',
              '"I clicked Script Builder and got an error or blank screen" — Script Builder is only available to Team Leads and Admins, not Agents.',
              '"I can\'t see the Leads page" — The full Leads table is only available to Team Leads and Admins. Agents see "My Leads" which shows only their own leads.',
              '"The page keeps loading but nothing appears" — Your session may have expired. Try signing out and signing back in.'
            ]
          },
          {
            type: 'heading',
            text: 'What To Do'
          },
          {
            type: 'steps',
            items: [
              'Check your role badge at the top of the sidebar — it shows "Agent," "Team Lead," or "Admin"',
              'If you need access to a feature not available in your role, contact your Team Lead and explain what you need',
              'Try signing out and back in — sometimes your permissions need a refresh after changes are made',
              'If the problem continues after signing back in, try a different browser (Chrome is recommended)',
              'If nothing helps, use "Send Feedback" in the sidebar to report the issue'
            ]
          },
          {
            type: 'heading',
            text: 'What To Tell Your Team Lead'
          },
          {
            type: 'paragraph',
            text: 'When asking your Team Lead for help, mention: (1) the page or feature you\'re trying to access, (2) what you see on screen (blank page, error message, spinning loader), and (3) which browser you\'re using. This helps them resolve it quickly.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'getting-started', articleId: 'understanding-roles', title: 'Understanding Your Role' },
              { sectionId: 'getting-started', articleId: 'logging-in', title: 'Logging In / First Login' }
            ]
          }
        ]
      },
      {
        id: 'reporting-problems',
        title: 'Reporting a Problem',
        roles: ALL_ROLES,
        keywords: ['report', 'problem', 'bug', 'issue', 'help', 'support', 'feedback'],
        content: [
          {
            type: 'paragraph',
            text: 'The fastest way to get help is to use the built-in "Send Feedback" feature. It automatically captures important context so you don\'t have to be technical.'
          },
          {
            type: 'heading',
            text: 'How to Report'
          },
          {
            type: 'steps',
            items: [
              'Click "Send Feedback" at the very bottom of the left sidebar',
              'Choose the type: Bug Report (something broke), Feature Request (suggest an improvement), or General Feedback',
              'Describe what happened in simple terms — e.g., "I clicked Save and got a red error message"',
              'Click "Send Feedback" to submit'
            ]
          },
          {
            type: 'heading',
            text: 'What\'s Captured Automatically'
          },
          {
            type: 'paragraph',
            text: 'You don\'t need to attach anything manually. The system automatically captures:'
          },
          {
            type: 'list',
            items: [
              'A screenshot of the current page at the moment you click "Send Feedback"',
              'Your browser type and version',
              'Which page you were on and your user role',
              'A log of recent errors that appeared in the background',
              'The time and date of the report'
            ]
          },
          {
            type: 'heading',
            text: 'Tips for Good Reports'
          },
          {
            type: 'list',
            items: [
              'Send the report immediately after the problem happens — the screenshot and error log are most helpful when fresh',
              'Describe what you were doing just before the problem: e.g., "I was on step 3 of the call script and clicked the blue Yes button"',
              'Note whether the problem happens every time or just sometimes',
              'Include the exact wording of any error message you see on screen'
            ]
          }
        ]
      },
      {
        id: 'forgot-password',
        title: 'Forgot Password',
        roles: ALL_ROLES,
        keywords: ['forgot', 'password', 'reset', 'email', 'change password', 'can\'t sign in'],
        content: [
          {
            type: 'paragraph',
            text: 'If you\'ve forgotten your password, you can reset it from the login page in just a few steps.'
          },
          {
            type: 'steps',
            items: [
              'Go to the TreeGenie login page and click the "Sign In" button',
              'On the sign-in form, click "Forgot your password?" (below the password field)',
              'Enter the email address you use to log into TreeGenie',
              'Click "Send Reset Email"',
              'Check your email inbox for a message from TreeGenie — also check your spam or junk folder',
              'Click the link in the email to open the password reset page',
              'Enter and confirm your new password, then click Save',
              'Go back to the login page and sign in with your new password'
            ]
          },
          {
            type: 'note',
            text: 'Password reset links expire after 1 hour. If you wait too long or the link shows an error, go back to the login page and request a new one.'
          },
          {
            type: 'tip',
            text: 'Make sure you\'re using the same email address your Team Lead used to invite you. If the reset email never arrives (even in spam), the email may be spelled differently in the system — contact your Team Lead to verify.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'getting-started', articleId: 'logging-in', title: 'Logging In / First Login' }
            ]
          }
        ]
      },
      {
        id: 'page-wont-load',
        title: 'Page Won\'t Load / Blank Screen',
        roles: ALL_ROLES,
        keywords: ['blank', 'white screen', 'loading', 'stuck', 'frozen', 'not loading', 'broken'],
        content: [
          {
            type: 'paragraph',
            text: 'If TreeGenie shows a blank white screen, gets stuck on a loading spinner, or stops responding, try these steps in order.'
          },
          {
            type: 'heading',
            text: 'Quick Fixes to Try First'
          },
          {
            type: 'steps',
            items: [
              'Hard refresh — Press Cmd+Shift+R on Mac or Ctrl+Shift+R on Windows. This reloads the page and clears cached files.',
              'Check your internet — Open another website (e.g., google.com) to confirm your connection is working.',
              'Try a different browser — If you\'re on Safari or Firefox, try Chrome. Chrome works best with TreeGenie.',
              'Try Incognito / Private mode — Open an incognito window (Cmd+Shift+N on Chrome Mac, Ctrl+Shift+N on Windows) and go to the TreeGenie URL. This bypasses browser extensions and cached data.',
              'Clear your cache — In Chrome: Settings > Privacy > Clear browsing data > Select "Cached images and files" > Clear data. Then reload TreeGenie.'
            ]
          },
          {
            type: 'heading',
            text: 'If None of That Works'
          },
          {
            type: 'list',
            items: [
              'Try a completely different device or computer',
              'Check if TreeGenie is down — ask a teammate if they\'re experiencing the same issue',
              'Use "Send Feedback" from a working session, or contact your Team Lead directly'
            ]
          },
          {
            type: 'note',
            text: 'Most blank screen issues are caused by browser caching problems or extensions interfering. Chrome in Incognito mode resolves this in most cases.'
          }
        ]
      },
      {
        id: 'invite-email-not-received',
        title: 'Invite Email Never Arrived',
        roles: LEAD_AND_ADMIN,
        keywords: ['invite', 'email', 'not received', 'missing', 'invitation', 'pending', 'sign up', 'resend', 'copy link'],
        content: [
          {
            type: 'paragraph',
            text: 'If a new team member says they never received their invitation email, don\'t re-invite them — use the invite link instead. Re-inviting can cause issues if they already have a pending account.'
          },
          {
            type: 'heading',
            text: 'Step 1 — Check Their Status First'
          },
          {
            type: 'paragraph',
            text: 'Go to Team Manager and find the person in the table. Their status tells you exactly what\'s happening:'
          },
          {
            type: 'list',
            items: [
              '"Pending Sign Up" — the invitation was created but they haven\'t signed up yet. Use the Copy Invite Link method below.',
              '"Active" — they already signed up successfully. Ask them to try signing in with the email you invited.',
              'Their name doesn\'t appear — something went wrong with the invitation. You can safely invite them again.'
            ]
          },
          {
            type: 'heading',
            text: 'Step 2 — Send the Invite Link Directly'
          },
          {
            type: 'steps',
            items: [
              'Find their row in the Team Manager table (status shows "Pending Sign Up")',
              'Click the "🔗 Copy Invite Link" button in the Actions column',
              'Paste the link into a text message, WhatsApp, Slack, or any other channel',
              'Tell them to open the link and sign up using the exact email address you invited'
            ]
          },
          {
            type: 'tip',
            text: 'The invite link bypasses email entirely — it\'s the most reliable way to get someone onto your team, especially if their email provider is filtering automated messages.'
          },
          {
            type: 'heading',
            text: 'Why Invitation Emails Sometimes Don\'t Arrive'
          },
          {
            type: 'list',
            items: [
              'The email was filtered into Spam or Junk — ask them to check those folders and mark it as "Not spam"',
              'Corporate email systems (Microsoft 365, Google Workspace with strict filtering) may block automated invitations',
              'The email address was entered with a typo — check the address in Team Manager carefully',
              'Some email providers delay delivery by several minutes — ask them to wait 5–10 minutes before concluding it\'s missing'
            ]
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'team-manager', articleId: 'inviting-team-members', title: 'Inviting Team Members' },
              { sectionId: 'team-manager', articleId: 'managing-team-members', title: 'Managing Team Members' }
            ]
          }
        ]
      },
      {
        id: 'wrong-or-missing-script',
        title: 'Call Script Shows Wrong or No Script',
        roles: ALL_ROLES,
        keywords: ['wrong script', 'missing script', 'no script', 'blank call script', 'can\'t start call', 'script not loading', 'template'],
        content: [
          {
            type: 'paragraph',
            text: 'If the Call Script page is blank, shows a "No script assigned" message, or loads the wrong script, one of a few things is going on.'
          },
          {
            type: 'heading',
            text: 'No Script Assigned'
          },
          {
            type: 'paragraph',
            text: 'This is the most common reason. When your Team Lead invited you, they may not have assigned a script at that time, or the script was assigned later and your session hasn\'t refreshed.'
          },
          {
            type: 'steps',
            items: [
              'Sign out and sign back in — your script assignment is loaded at login, so a fresh session picks up any recent changes',
              'If it\'s still missing after signing back in, ask your Team Lead to confirm you have a script assigned in Team Manager',
              'Your Team Lead can change your script assignment from Team Manager → find your row → click the template name to update it'
            ]
          },
          {
            type: 'heading',
            text: 'Wrong Script Is Showing'
          },
          {
            type: 'paragraph',
            text: 'If you see a script but it\'s not the right one for today\'s calls, your Team Lead needs to reassign you to the correct script. Scripts are assigned per user, not per call.'
          },
          {
            type: 'steps',
            items: [
              'Let your Team Lead know which script you should be using',
              'They can update it in Team Manager in under a minute — go to your row and click the template name',
              'Once changed, sign out and back in for the new script to appear'
            ]
          },
          {
            type: 'heading',
            text: 'Script Loads But Has Outdated Content'
          },
          {
            type: 'paragraph',
            text: 'If the script looks like an old version, your browser may have cached a previous copy. Do a hard refresh (Cmd+Shift+R on Mac, Ctrl+Shift+R on Windows) to force it to reload the latest version.'
          },
          {
            type: 'note',
            text: 'Agents cannot change their own script assignment — only Team Leads and Admins can do this from Team Manager.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'starting-a-call', title: 'Starting a Call' },
              { sectionId: 'team-manager', articleId: 'managing-team-members', title: 'Managing Team Members' }
            ]
          }
        ]
      },
      {
        id: 'cant-save-lead',
        title: 'Can\'t Save a Lead',
        roles: ALL_ROLES,
        keywords: ['save lead', 'save button', 'lead not saving', 'required fields', 'grayed out', 'error saving', 'submit'],
        content: [
          {
            type: 'paragraph',
            text: 'If the "Save & Start New Call" button isn\'t responding, is grayed out, or shows an error when clicked, here\'s how to fix it.'
          },
          {
            type: 'heading',
            text: 'Button Is Grayed Out'
          },
          {
            type: 'paragraph',
            text: 'The save button stays disabled until all required fields are filled in. Look for fields with a red border or a "required" message — fill those in and the button will become active.'
          },
          {
            type: 'list',
            items: [
              'Required fields are: Prospect Name and Phone Number',
              'Make sure the phone number contains at least 10 digits — don\'t include letters or special characters other than dashes, spaces, or parentheses',
              'Scroll down in the lead form — there may be required fields below the visible area'
            ]
          },
          {
            type: 'heading',
            text: 'Button Clicks But Shows a Red Error'
          },
          {
            type: 'steps',
            items: [
              'Check your internet connection — saving requires a live connection to the server',
              'Try clicking Save again after a moment — a brief connection hiccup can cause a single failed save',
              'If the error says "permission denied," sign out and back in to refresh your session credentials',
              'If you see a specific error message, take a screenshot and use "Send Feedback" to report it'
            ]
          },
          {
            type: 'heading',
            text: 'Lead Saved But Disappeared'
          },
          {
            type: 'paragraph',
            text: 'After saving, the call form resets automatically (this is intentional — it prepares you for the next call). Your lead is still saved. Go to the Leads page to confirm it was recorded.'
          },
          {
            type: 'tip',
            text: 'If you\'re in the middle of a call and the page reloads unexpectedly, the in-progress lead data is NOT automatically saved. You\'ll need to re-enter the information and save manually.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-script', articleId: 'saving-a-lead', title: 'Saving a Lead' },
              { sectionId: 'troubleshooting', articleId: 'connection-issues', title: 'Connection Issues' }
            ]
          }
        ]
      },
      {
        id: 'audio-upload-problems',
        title: 'Audio Upload Problems',
        roles: ALL_ROLES,
        keywords: ['audio', 'upload', 'call evaluator', 'file', 'mp3', 'wav', 'm4a', 'too large', 'not uploading', 'upload failed', 'evaluation error'],
        content: [
          {
            type: 'paragraph',
            text: 'If your call recording won\'t upload to the Call Evaluator, or the upload fails partway through, the cause is usually the file format, file size, or a connection issue.'
          },
          {
            type: 'heading',
            text: 'Supported File Formats'
          },
          {
            type: 'paragraph',
            text: 'The Call Evaluator accepts the following audio formats:'
          },
          {
            type: 'list',
            items: [
              'MP3 — most commonly used, widely supported',
              'MP4 / M4A — common on iPhones and iOS voice recorders',
              'WAV — high quality, but files are large',
              'OGG, WebM — supported but less common',
              'Maximum file size: 25MB'
            ]
          },
          {
            type: 'heading',
            text: 'File Too Large'
          },
          {
            type: 'paragraph',
            text: 'If your recording is over 25MB, compress it before uploading. Most calls are well under this limit, but long recordings or WAV files can exceed it.'
          },
          {
            type: 'list',
            items: [
              'Use a free tool like Audacity, GarageBand, or an online converter to export as MP3 at 128kbps',
              'A 30-minute MP3 at 128kbps is roughly 28MB — keep calls under 25 minutes or export at a lower bitrate (e.g., 64kbps for voice)',
              'If you\'re recording with your phone, check if there\'s a quality/format setting you can lower'
            ]
          },
          {
            type: 'heading',
            text: 'Upload Starts But Then Fails'
          },
          {
            type: 'steps',
            items: [
              'Check your internet connection — large file uploads require a stable connection throughout',
              'Don\'t navigate away from the page while the upload is in progress',
              'Try using Chrome — it handles large file uploads more reliably than Safari or Firefox',
              'If on a corporate network, ask your IT team if large file uploads are restricted',
              'Try uploading a shorter test recording first to see if the problem is file-specific'
            ]
          },
          {
            type: 'heading',
            text: 'Evaluation Limit Reached'
          },
          {
            type: 'paragraph',
            text: 'If your upload succeeds but the evaluation doesn\'t start, you may have used all the evaluations included in your plan for this month. Check Settings → Subscription to see your usage. Your Team Lead or Admin can purchase additional evaluation credits.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'call-evaluator', articleId: 'uploading-a-call', title: 'Uploading a Call for Evaluation' },
              { sectionId: 'settings', articleId: 'subscription-billing', title: 'Subscription & Billing' }
            ]
          }
        ]
      },
      {
        id: 'calls-not-in-analytics',
        title: 'My Calls Aren\'t Showing in Analytics',
        roles: ALL_ROLES,
        keywords: ['analytics', 'missing calls', 'not showing', 'command center', 'dashboard', 'leaderboard', 'stats not updating', 'zero calls'],
        content: [
          {
            type: 'paragraph',
            text: 'If your calls aren\'t appearing on the leaderboard or in Command Center analytics, there are a few common reasons.'
          },
          {
            type: 'heading',
            text: 'Calls Are Tracked When You Save a Lead or Complete a Call'
          },
          {
            type: 'paragraph',
            text: 'Analytics are recorded automatically when you finish a call — either by clicking "Save & Start New Call" (for completed calls where you collected lead info) or when a call ends without saving. Just navigating through the script without saving does not generate analytics.'
          },
          {
            type: 'heading',
            text: 'Common Reasons Calls Don\'t Appear'
          },
          {
            type: 'list',
            items: [
              'The call was started but the page was closed or refreshed before saving — in-progress data is not recorded',
              'You\'re viewing the wrong date range — Command Center defaults to the last 30 days; check the date filter',
              'You\'re looking at a different team\'s data — confirm the correct team is selected in the Command Center filter',
              'Analytics can take up to a few minutes to update after saving — try refreshing the Command Center page',
              'You\'re using Safari, which handles background sync differently — try Chrome for more reliable real-time updates'
            ]
          },
          {
            type: 'heading',
            text: 'Calls Show in Leads But Not Analytics'
          },
          {
            type: 'paragraph',
            text: 'The Leads page and the analytics engine are updated separately. If a lead appears in your Leads list but the call count in Command Center seems low, it may be a display lag. Wait a few minutes and do a hard refresh (Cmd+Shift+R / Ctrl+Shift+R) on the Command Center page.'
          },
          {
            type: 'tip',
            text: 'If the problem persists across multiple days and calls, use "Send Feedback" to report it. Include the date range of the missing calls and your email address so the issue can be investigated.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'command-center', articleId: 'command-center-overview', title: 'Command Center Overview' },
              { sectionId: 'troubleshooting', articleId: 'reporting-problems', title: 'Reporting a Problem' }
            ]
          }
        ]
      },
      {
        id: 'signed-out-unexpectedly',
        title: 'Getting Signed Out Unexpectedly',
        roles: ALL_ROLES,
        keywords: ['signed out', 'logged out', 'session expired', 'kicked out', 'safari', 'keep signing out', 'session timeout', 'automatic logout'],
        content: [
          {
            type: 'paragraph',
            text: 'If you keep getting signed out of TreeGenie without choosing to, there are several possible causes depending on your browser and setup.'
          },
          {
            type: 'heading',
            text: 'Safari Users'
          },
          {
            type: 'paragraph',
            text: 'Safari handles login sessions differently from Chrome and Firefox. In Safari, your session is tied to the browser tab — closing the tab or the browser window will end your session, requiring you to sign back in.'
          },
          {
            type: 'list',
            items: [
              'Don\'t close the TreeGenie tab — minimize it instead if you need to step away',
              'Use Chrome or Firefox for a persistent session that survives tab closures',
              'If you must use Safari, bookmark the page and sign back in quickly each time — your team data and scripts will be exactly where you left them'
            ]
          },
          {
            type: 'heading',
            text: 'Session Timeout (All Browsers)'
          },
          {
            type: 'paragraph',
            text: 'For security, sessions automatically expire after 12 hours of inactivity. If you leave TreeGenie open overnight and come back in the morning, you may need to sign back in. This is expected behavior.'
          },
          {
            type: 'heading',
            text: 'Browser Privacy Settings or Extensions'
          },
          {
            type: 'list',
            items: [
              'Privacy-focused browser extensions (like Privacy Badger, uBlock, or certain VPNs) can sometimes block the cookies that keep you logged in',
              'If you\'re getting signed out frequently and the above don\'t apply, try Chrome in Incognito mode (which disables extensions) to see if the problem goes away',
              'If Incognito fixes it, disable your browser extensions one by one to find the culprit'
            ]
          },
          {
            type: 'heading',
            text: 'Multiple Accounts or Devices'
          },
          {
            type: 'paragraph',
            text: 'If you use TreeGenie on multiple devices (e.g., desktop and laptop), signing in on one device can sometimes affect your session on another. This is most noticeable on Safari. If this happens, just sign in again on the device you\'re using.'
          },
          {
            type: 'note',
            text: 'If you\'re getting signed out mid-call, always try to complete any in-progress lead saves first. Unsaved call data is lost when a session ends.'
          },
          {
            type: 'relatedArticles',
            links: [
              { sectionId: 'getting-started', articleId: 'logging-in', title: 'Logging In / First Login' },
              { sectionId: 'troubleshooting', articleId: 'connection-issues', title: 'Connection Issues' }
            ]
          }
        ]
      }
    ]
  }
];

export default helpSections;
